/**
 * CMMD API client — unified transport-backed implementation.
 *
 * Replaces the hand-rolled CmmdApiClient in cmmdApiClient.ts.
 * All requests go through `request<T>` (transport.ts), which handles:
 *   - credentials: "include" always
 *   - CMMD envelope unwrap ({ data, meta } → data)
 *   - 401 → onAuthExpired + CmmdApiError { kind: "auth" }
 *   - 403 → onAuthExpired (no redirect) + CmmdApiError { kind: "permission" }
 *   - 429/5xx GET → exponential backoff retry
 *   - 204 / empty → undefined
 */

import {
  type AuthRuntimeApi,
  connectAuthRuntime,
  destroyAuthRuntime,
  getAuthRuntimeState,
  provisionAuthRuntime,
} from "./authRuntime";
import { fetchBilling, fetchBillingSeries, type BillingReadInput } from "./billing";
import { fetchBootstrap, type BootstrapReadInput } from "./bootstrap";
import {
  removeLinkedProviderAccount,
  disconnectUserClaudeCredential,
  disconnectUserCodexCredential,
  getUserClaudeCredential,
  getUserCodexCredential,
} from "./providerCredentials";
import { bindThreadDispositionApi, type ThreadDispositionApi } from "./threadDisposition";
import { bindThreadAttachmentsApi, type ThreadAttachmentsApi } from "./threadAttachments";
import { legacyRequest } from "./legacyRequest";
export { CmmdApiAuthError, CmmdApiServerError } from "./legacyRequest";
import { bindThreadArchiveApi, type ThreadArchiveApi } from "./threadArchive";
import { bindUserAccountApi, type UserAccountApi } from "./userAccountApi";
import { request, CmmdApiError, type TransportConfig } from "./transport";
import { Schema } from "effect";
import { handleCmmdAuthExpired } from "./cmmdAuthRedirect";
import { renameCloudEnvironment, renameDevice, revokeDevice } from "./registryNaming";
import {
  buildRuntimeInventoryQuery,
  type GetCmmdRuntimeInventoryParams,
} from "./runtimeInventoryQuery";
import { readPrimaryEnvironmentTarget, resolvePrimaryEnvironmentHttpUrl } from "../primary/target";
import type { CmmdRenamedForgeCloudEnvironment } from "../primary/cloudProjectCatalog";
import {
  embedThreadViewerExampleReplica,
  isEmbedThreadViewerExample,
  withEmbedThreadViewerExampleInventory,
} from "../../cmmd/embedThreadViewerExample";
import { readBooleanField, readIdField, readStringField } from "./fieldReaders";
import { CmmdProjectContextLinksResponseSchema } from "@cmmd-forge/contracts";
import type {
  CmmdAttachment,
  CmmdBoardTask,
  CmmdProjectAgentMember,
  CmmdProjectAgentInstance,
  CreateCmmdProjectAgentMemberParams,
  CreateCmmdProjectAgentMemberResult,
  CmmdProjectAgentMemberConsolidationPreview,
  ApplyCmmdProjectAgentMemberConsolidationParams,
  CmmdProjectAgentMemberConsolidationResult,
  RenameCmmdProjectAgentMemberParams,
  RenameCmmdProjectAgentMemberResult,
  RetireCmmdProjectAgentMemberParams,
  RetireCmmdProjectAgentMemberResult,
  UpdateCmmdProjectAgentMemberAvatarParams,
  UpdateCmmdProjectAgentMemberAvatarResult,
  CmmdProjectTaskAssignmentParams,
  CmmdProjectTaskAssignmentResult,
  CmmdCenterBoardResponse,
  CmmdBootstrapData,
  CmmdUsageSummaryInput,
  UsageSummary,
  CmmdBillingContext,
  CmmdBillingSeries,
  CmmdCreateForgeAutomationRuleParams,
  CmmdFeedbackIntakeResult,
  CmmdForgeTaskDetail,
  CreateCmmdFeedbackParams,
  CmmdForgeAutomationPreset,
  CmmdForgeAutomationRule,
  CmmdForgeAutomationRun,
  CmmdForgeAutomationRunDetail,
  CmmdUserForgeAutomationRule,
  CmmdUserForgeAutomationRun,
  CmmdCreateUserForgeAutomationRuleParams,
  CmmdUpdateUserForgeAutomationRuleParams,
  CmmdStartUserForgeAutomationRunParams,
  CmmdStartUserForgeAutomationRunResult,
  CmmdStartForgeAutomationRunParams,
  CmmdStartForgeAutomationRunResult,
  CmmdRetryForgeAutomationRunParams,
  CmmdRetryForgeAutomationRunResult,
  CmmdCancelForgeAutomationRunParams,
  CmmdCancelForgeAutomationRunResult,
  CmmdApproveForgeAutomationEscalationParams,
  CmmdApproveForgeAutomationEscalationResult,
  CmmdRejectForgeAutomationEscalationParams,
  CmmdRejectForgeAutomationEscalationResult,
  CmmdProfileData,
  CmmdOrgProviderPolicy,
  CmmdCodexCredentialState,
  CmmdClaudeCredentialState,
  CmmdProviderAccountInventory,
  CmmdProviderUsageSnapshots,
  CmmdClaudeRuntimeReadiness,
  CmmdBrainDoc,
  CmmdCampaign,
  CmmdComment,
  CmmdDeviceRegistryDevice,
  CmmdDeviceRegistrySnapshot,
  CmmdRuntimeDiagnosticsSnapshot,
  CmmdCloudProjectLifecyclePreflight,
  CmmdCloudProjectLifecyclePreflightResponse,
  CmmdRuntimeInventoryRuntime,
  CmmdRuntimeInventorySnapshot,
  CmmdMember,
  AddCmmdProjectMemberParams,
  AddCmmdProjectMemberResult,
  CmmdProjectMilestone,
  CreateCmmdProjectMilestoneParams,
  UpdateCmmdProjectMilestoneParams,
  CmmdTaskComment,
  CreateCmmdTaskCommentParams,
  DeleteCmmdTaskCommentParams,
  CmmdTaskCommentDeleteReceipt,
  UpdateCmmdTaskCommentParams,
  CmmdProjectContentReadabilityResult,
  CmmdProjectContentSurface,
  CmmdProjectUpdateStatus,
  CmmdProjectUpdateView,
  CmmdProjectUpdatesPage,
  CmmdEnvelope,
  CmmdMoveReport,
  CmmdProject,
  CmmdProjectContextLink,
  CmmdEnsuredGitHubProjectContext,
  CmmdProjectRepository,
  CmmdProjectRepositories,
  CreateCmmdBoardTaskParams,
  CreateCmmdCenterBoardTaskParams,
  ListCmmdCenterBoardParams,
  MoveCmmdCenterBoardTaskParams,
  MoveCmmdProjectBoardTaskParams,
  UpdateCmmdBoardTaskParams,
  CmmdTaskExecutionOption,
  CmmdWorkSession,
  CmmdCreateWorkSessionParams,
  CmmdCreateWorkSessionResult,
  CmmdListWorkSessionsParams,
  CmmdWorkSessionStatus,
  CmmdAttachWorkSessionThreadParams,
  CmmdAttachWorkSessionThreadResult,
  CmmdTaskConversation,
  CmmdTaskHistory,
  UpsertCmmdTaskPullRequestParams,
  UpsertCmmdTaskPullRequestResult,
  CmmdPullRequestListInput,
  CmmdPullRequestListResult,
  CmmdPullRequestListStatsInput,
  CmmdPullRequestListStatsResult,
  CmmdPullRequestRef,
  CmmdPullRequestDetail,
  CmmdPullRequestActivity,
  CmmdPullRequestDiffInput,
  CmmdPullRequestDiffResult,
  CmmdPullRequestDiffFileContentsInput,
  CmmdPullRequestDiffFileContentsResult,
  CmmdPullRequestThreadCommentsInput,
  CmmdPullRequestThreadCommentsResult,
  CmmdPullRequestReviewerCandidatesInput,
  CmmdPullRequestReviewerCandidatesResult,
  CmmdPullRequestActionInput,
  CmmdPullRequestCommentInput,
  CmmdPullRequestUpdateInput,
  CmmdPullRequestCommentUpdateInput,
  CmmdPullRequestReviewInput,
  CmmdPullRequestThreadReplyInput,
  CmmdPullRequestThreadResolutionInput,
  CmmdPullRequestReviewersInput,
  CmmdPullRequestReactionInput,
  CmmdPullRequestMutationResult,
  CmmdTaskConversationList,
  CmmdTaskConversationListParams,
  CmmdTaskConversationParams,
  LinkCmmdTaskConversationParams,
  LinkCmmdTaskConversationResult,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
  CmmdTask,
  CmmdUpdateForgeAutomationRuleParams,
  RegisterCmmdDeviceParams,
  ReplaceCmmdRuntimeInventoryParams,
  AppendCmmdRuntimeThreadEventsParams,
  AppendCmmdRuntimeThreadEventsResult,
  CmmdRuntimeThreadReplica,
  CmmdRelayControlRoute,
  CreateCmmdRelayControlRouteParams,
  CmmdRelayActionGrant,
  CreateCmmdRelayActionGrantParams,
  CmmdRuntimeAccessGrant,
  CmmdRuntimeAccessRevokeResult,
  GrantCmmdRuntimeAccessParams,
  CmmdSavedDataView,
  CreateCmmdSavedDataViewParams,
  DeleteCmmdSavedDataViewParams,
  ImportCmmdSavedDataViewParams,
  ImportCmmdSavedDataViewResult,
  ListCmmdSavedDataViewsParams,
  UpdateCmmdSavedDataViewParams,
  CmmdProjectLeadPlan,
  CmmdProjectTaskDraft,
  CreateCmmdProjectTaskDraftParams,
  UpdateCmmdProjectTaskDraftParams,
  PreviewCmmdProjectTaskDraftParams,
  CmmdProjectTaskDraftPreview,
  CommitCmmdProjectTaskDraftParams,
  CmmdProjectTaskDraftCommit,
  DiscardCmmdProjectTaskDraftParams,
  ActivateCmmdProjectLeadAuthorityResult,
  CmmdProjectLeadReadiness,
  CmmdProjectLeadCapabilities,
  CmmdProjectLeadSummary,
  CmmdProjectLeadDetail,
  CmmdProjectLeadBriefing,
  DecideCmmdProjectLeadApprovalParams,
  DecideCmmdProjectLeadApprovalResult,
  CmmdProjectLeadEvents,
  CmmdProjectLeadEventsParams,
  CmmdProjectLeadExplanation,
  PauseCmmdProjectLeadParams,
  PauseCmmdProjectLeadResult,
  ResumeCmmdProjectLeadParams,
  ResumeCmmdProjectLeadResult,
  ReserveCmmdProjectLeadTaskParams,
  ReserveCmmdProjectLeadTaskResult,
  ReleaseCmmdProjectLeadReservationParams,
  ReleaseCmmdProjectLeadReservationResult,
  ReconcileCmmdProjectLeadParams,
  ReconcileCmmdProjectLeadResult,
  ResolveCmmdProjectLeadAttentionParams,
  ResolveCmmdProjectLeadAttentionResult,
  CmmdAuthRuntimeState,
  CmmdAuthRuntimeConnectDescriptor,
  CmmdProjectPatchBody,
} from "@cmmd-forge/contracts";
import { CMMD_PROJECT_PATCH_FIELD_NAMES, CMMD_PROJECT_PATCH_FIELDS } from "@cmmd-forge/contracts";

export type { CmmdMoveReport } from "@cmmd-forge/contracts";

function taskConversationQuery(params: {
  readonly limit?: number;
  readonly cursor?: string | null;
}): Record<string, string | number | undefined> {
  return {
    ...(params.limit !== undefined ? { limit: params.limit } : {}),
    ...(params.cursor ? { cursor: params.cursor } : {}),
  };
}

// ── Re-exports ────────────────────────────────────────────────────────────────

export { CmmdApiError } from "./transport";

export class CmmdApiNotImplementedError extends Error {
  constructor(methodName: string) {
    super(`CmmdApiClient.${methodName} is not implemented yet`);
    this.name = "CmmdApiNotImplementedError";
  }
}

export class CmmdProjectLeadCapabilityError extends Error {
  constructor(readonly access: "read" | "activate") {
    super(`CMMD Project Lead does not advertise compatible ${access} access.`);
    this.name = "CmmdProjectLeadCapabilityError";
  }
}

/**
 * Fail-closed compatibility gate for Project Lead consumers. A future CMMD
 * major contract must not be treated as compatible by an older Forge client.
 */
export function supportsCmmdProjectLead(
  capabilities: CmmdProjectLeadCapabilities,
  access: "read" | "activate",
): boolean {
  if (capabilities.schemaVersion !== 1 || capabilities.product !== "cmmd") return false;
  const operationCapability = capabilities.capabilities.find(
    (capability) => capability.name === "forge_context_operations",
  );
  if (!operationCapability || operationCapability.version.major !== 1) return false;
  return access === "activate"
    ? operationCapability.supportsActivation
    : operationCapability.supportsReadOnly;
}

export function requireCmmdProjectLead(
  capabilities: CmmdProjectLeadCapabilities,
  access: "read" | "activate",
): void {
  if (!supportsCmmdProjectLead(capabilities, access)) {
    throw new CmmdProjectLeadCapabilityError(access);
  }
}

function readCmmdCollection<T>(value: unknown, key: "campaigns" | "projects"): ReadonlyArray<T> {
  if (Array.isArray(value)) return value as ReadonlyArray<T>;
  if (value && typeof value === "object") {
    const keyed = (value as Record<string, unknown>)[key];
    if (Array.isArray(keyed)) return keyed as ReadonlyArray<T>;
  }
  return [];
}

function readCmmdAutomationCollection<T>(
  value: unknown,
  key: "presets" | "rules" | "runs",
): ReadonlyArray<T> {
  if (Array.isArray(value)) return value as ReadonlyArray<T>;
  if (value && typeof value === "object") {
    const keyed = (value as Record<string, unknown>)[key];
    if (Array.isArray(keyed)) return keyed as ReadonlyArray<T>;
  }
  return [];
}

export interface CmmdForgeAutomationScope {
  readonly organizationId?: string;
}

function forgeAutomationQuery(
  scope: CmmdForgeAutomationScope | undefined,
  taskId?: number,
): Record<string, string> | undefined {
  const query: Record<string, string> = {};
  if (scope?.organizationId) query["orgId"] = scope.organizationId;
  if (taskId !== undefined) query["taskId"] = String(taskId);
  return Object.keys(query).length > 0 ? query : undefined;
}

function normalizeCmmdMember(row: unknown): CmmdMember {
  const first = readStringField(row, "firstName") ?? "";
  const last = readStringField(row, "lastName") ?? "";
  const name =
    `${first} ${last}`.trim() ||
    readStringField(row, "username") ||
    readStringField(row, "email") ||
    "Unknown";
  return {
    id: readIdField(row, "userId") ?? readIdField(row, "id") ?? name,
    name,
    email: readStringField(row, "email"),
    role: readStringField(row, "role"),
    avatarUrl: readStringField(row, "profileImage") ?? readStringField(row, "avatar"),
  };
}

function normalizeCmmdWorkspaceMember(row: unknown): CmmdMember {
  const user = readObjectField(row, "user") ?? row;
  const normalized = normalizeCmmdMember({
    ...(typeof user === "object" && user !== null ? user : {}),
    userId: readIdField(row, "userId") ?? readIdField(user, "id"),
    role: readStringField(row, "role"),
  });
  return normalized;
}

/**
 * Map a raw forge-context project-update row ({ status, body, authorFirstName,
 * authorProfileImage, createdAt, … }) to the CmmdProjectUpdate the UI reads.
 * Unknown statuses fall back to "on_track" so a bad value never breaks render.
 */
function normalizeCmmdProjectUpdate(row: unknown): CmmdProjectUpdateView {
  const first = readStringField(row, "authorFirstName") ?? "";
  const last = readStringField(row, "authorLastName") ?? "";
  const statusRaw = readStringField(row, "status");
  const status: CmmdProjectUpdateStatus =
    statusRaw === "at_risk" || statusRaw === "off_track" || statusRaw === "complete"
      ? statusRaw
      : "on_track";
  return {
    id: readIdField(row, "id") ?? "",
    status,
    body: readStringField(row, "body") ?? "",
    authorName: `${first} ${last}`.trim() || null,
    authorAvatarUrl: readStringField(row, "authorProfileImage") ?? null,
    createdAt: readStringField(row, "createdAt") ?? "",
    updatedAt: readStringField(row, "updatedAt") ?? readStringField(row, "createdAt") ?? "",
    canEdit: readBooleanField(row, "canEdit") ?? false,
  };
}

function normalizeProjectUpdatesPage(
  envelope: CmmdEnvelope<ReadonlyArray<unknown>>,
  requestedPage: number,
  requestedLimit: number,
): CmmdProjectUpdatesPage {
  const updates = Array.isArray(envelope.data) ? envelope.data.map(normalizeCmmdProjectUpdate) : [];
  const pagination = envelope.pagination;
  return {
    updates,
    pagination: pagination ?? {
      page: requestedPage,
      limit: requestedLimit,
      total: updates.length,
      hasMore: false,
    },
  };
}

function readObjectField(obj: unknown, key: string): Record<string, unknown> | null {
  if (!obj || typeof obj !== "object") return null;
  const value = (obj as Record<string, unknown>)[key];
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

const RELATION_AVATAR_URL_KEYS = [
  "avatarUrl",
  "avatar_url",
  "profileImage",
  "profile_image",
  "profileImageUrl",
  "profile_image_url",
  "image",
  "imageUrl",
  "image_url",
  "picture",
] as const;

function readRelationAvatarUrlField(obj: unknown, key: string): string | null {
  const relation = readObjectField(obj, key);
  if (!relation) return null;
  for (const field of RELATION_AVATAR_URL_KEYS) {
    const value = readStringField(relation, field);
    if (value) return value;
  }
  return null;
}

function readRelationIdField(obj: unknown, key: string): string | null {
  const relation = readObjectField(obj, key);
  return relation ? readIdField(relation, "id") : null;
}

function hasOwnField(obj: unknown, key: string): boolean {
  return Boolean(obj && typeof obj === "object" && Object.hasOwn(obj, key));
}

function normalizeCmmdCampaign(campaign: CmmdCampaign): CmmdCampaign {
  const normalized: CmmdCampaign = {
    ...campaign,
    id: readIdField(campaign, "id") ?? campaign.id,
    title: campaign.title ?? readStringField(campaign, "name") ?? "",
  };
  const workspaceId =
    readIdField(campaign, "workspaceId") ??
    readIdField(campaign, "workspace_id") ??
    readRelationIdField(campaign, "workspace") ??
    campaign.workspaceId ??
    null;
  if (workspaceId !== null || hasOwnField(campaign, "workspaceId")) {
    return {
      ...normalized,
      workspaceId,
      startDate: campaign.startDate ?? readStringField(campaign, "start_date") ?? null,
      endDate: campaign.endDate ?? readStringField(campaign, "end_date") ?? null,
    };
  }
  return normalized;
}

function normalizeCmmdProject(project: CmmdProject): CmmdProject {
  const ownerAvatarUrl =
    project.ownerAvatarUrl ??
    readStringField(project, "owner_avatar_url") ??
    readStringField(project, "ownerProfileImage") ??
    readStringField(project, "owner_profile_image") ??
    readStringField(project, "ownerImage") ??
    readStringField(project, "owner_image") ??
    readStringField(project, "ownerImageUrl") ??
    readStringField(project, "owner_image_url") ??
    readStringField(project, "createdByAvatarUrl") ??
    readStringField(project, "created_by_avatar_url") ??
    readStringField(project, "createdByProfileImage") ??
    readStringField(project, "created_by_profile_image") ??
    readRelationAvatarUrlField(project, "owner") ??
    readRelationAvatarUrlField(project, "createdBy") ??
    readRelationAvatarUrlField(project, "created_by") ??
    null;
  const normalized: Record<string, unknown> = {
    ...project,
    id: readIdField(project, "id") ?? project.id,
  };
  const campaignId =
    readIdField(project, "campaignId") ??
    readIdField(project, "campaign_id") ??
    readRelationIdField(project, "campaign") ??
    project.campaignId ??
    null;
  if (campaignId !== null || hasOwnField(project, "campaignId")) normalized.campaignId = campaignId;
  const campaignTitle =
    project.campaignTitle ??
    readStringField(project, "campaignTitle") ??
    readStringField(project, "campaign_title") ??
    readStringField(readObjectField(project, "campaign"), "title") ??
    readStringField(readObjectField(project, "campaign"), "name") ??
    null;
  if (campaignTitle !== null || hasOwnField(project, "campaignTitle")) {
    normalized.campaignTitle = campaignTitle;
  }
  const workspaceId =
    readIdField(project, "workspaceId") ??
    readIdField(project, "workspace_id") ??
    readRelationIdField(project, "workspace") ??
    project.workspaceId ??
    null;
  if (workspaceId !== null || hasOwnField(project, "workspaceId"))
    normalized.workspaceId = workspaceId;
  const workspaceName =
    project.workspaceName ??
    readStringField(project, "workspace_name") ??
    readStringField(readObjectField(project, "workspace"), "name") ??
    null;
  if (workspaceName !== null || hasOwnField(project, "workspaceName")) {
    normalized.workspaceName = workspaceName;
  }
  const workspaceIconUrl =
    readStringField(project, "workspaceIconUrl") ??
    readStringField(project, "workspace_icon_url") ??
    readStringField(readObjectField(project, "workspace"), "iconUrl") ??
    null;
  if (workspaceIconUrl !== null || hasOwnField(project, "workspaceIconUrl")) {
    normalized.workspaceIconUrl = workspaceIconUrl;
  }
  const ownerId =
    readIdField(project, "ownerId") ??
    readIdField(project, "owner_id") ??
    readRelationIdField(project, "owner") ??
    project.ownerId ??
    null;
  if (ownerId !== null || hasOwnField(project, "ownerId")) normalized.ownerId = ownerId;
  const ownerName =
    project.ownerName ??
    readStringField(project, "owner_name") ??
    readStringField(readObjectField(project, "owner"), "name") ??
    null;
  if (ownerName !== null || hasOwnField(project, "ownerName")) normalized.ownerName = ownerName;
  if (ownerAvatarUrl !== null || hasOwnField(project, "ownerAvatarUrl")) {
    normalized.ownerAvatarUrl = ownerAvatarUrl;
  }
  const teamId =
    readIdField(project, "teamId") ??
    readIdField(project, "team_id") ??
    readRelationIdField(project, "team") ??
    project.teamId ??
    null;
  if (teamId !== null || hasOwnField(project, "teamId")) normalized.teamId = teamId;
  const teamName =
    project.teamName ??
    readStringField(project, "team_name") ??
    readStringField(readObjectField(project, "team"), "name") ??
    null;
  if (teamName !== null || hasOwnField(project, "teamName")) normalized.teamName = teamName;
  const startDate = project.startDate ?? readStringField(project, "start_date") ?? null;
  if (startDate !== null || hasOwnField(project, "startDate")) normalized.startDate = startDate;
  const targetEndDate =
    project.targetEndDate ?? readStringField(project, "target_end_date") ?? null;
  if (targetEndDate !== null || hasOwnField(project, "targetEndDate"))
    normalized.targetEndDate = targetEndDate;
  return normalized as unknown as CmmdProject;
}

export interface SearchTasksParams {
  readonly q: string;
  readonly projectId?: string;
  readonly status?: string;
  readonly limit?: number;
}

export interface UpdateTaskParams {
  readonly status?: string | null;
  readonly priority?: string | null;
  readonly title?: string;
  readonly description?: string | null;
  readonly dueDate?: string | null;
  readonly assigneeId?: string | null;
  readonly labels?: ReadonlyArray<string>;
}

export interface CreateTaskParams {
  readonly title: string;
  readonly projectId?: string;
  readonly campaignId?: string;
  readonly description?: string;
  readonly priority?: string;
  readonly dueDate?: string;
}

export interface CreateSubtaskParams {
  readonly title: string;
  readonly parentTaskId: string;
}

export interface CreateBoardTaskParams {
  readonly title: string;
  readonly projectId: string;
}

export interface CreateProjectParams {
  readonly title: string;
  readonly campaignId?: string | null;
  readonly organizationId?: string | null;
  readonly description?: string | null;
  readonly brief?: string | null;
  readonly status?: string | null;
  readonly priority?: string | null;
}

export interface CreateCampaignParams {
  readonly title: string;
  readonly description?: string | null;
  readonly status?: string | null;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
}

export interface UpdateCampaignParams {
  readonly title?: string;
  readonly description?: string | null;
  readonly status?: string | null;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
}

export interface CreateCommentParams {
  readonly entityType: "task" | "project";
  readonly entityId: string;
  readonly content: string;
}

export interface BrainDocMeta {
  readonly title: string;
  readonly tags?: ReadonlyArray<string>;
}

export interface CreateBrainDocParams {
  readonly title: string;
  readonly content: string;
  readonly entityId?: string;
  readonly tags?: ReadonlyArray<string>;
  readonly sourceSystem?: string;
}

/**
 * Derived from the shared field list, not re-declared beside it.
 *
 * This interface used to be a hand-kept copy, which is half of how a field can
 * exist in the UI and never reach CMMD. See `cmmdProjectPatch`.
 */
export type UpdateProjectParams = CmmdProjectPatchBody;

export interface ProjectLinkData {
  readonly linked: boolean;
  readonly forgeEnvironmentId?: string;
  readonly forgeProjectId?: string;
  readonly forgeWorkspaceRoot?: string;
  readonly cmmdOrganizationId?: number;
  readonly cmmdProjectId?: number;
  readonly cmmdCampaignId?: number | null;
  readonly cmmdWorkspaceId?: number;
  readonly cmmdWorkspaceName?: string;
  readonly workspaceName?: string;
  readonly cmmdCampaignName?: string | null;
  readonly campaignName?: string | null;
  readonly cmmdProjectName?: string;
  readonly projectName?: string;
  readonly githubRepoFullName?: string | null;
  readonly source?: "linked_existing" | "created_from_cmmd" | "created_from_forge";
  readonly linkedByUserId?: string | null;
  readonly linkedAt?: string;
  readonly lastVerifiedAt?: string | null;
}

export interface WellKnownItem {
  readonly id: number;
  readonly name: string;
  readonly logoUrl?: string | null;
  readonly iconUrl?: string | null;
}

/** Campaign row from /.well-known/forge/cmmd/campaigns — carries dates for the timeline. */
export interface CmmdCampaignRow {
  readonly id: number;
  readonly name: string;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly status: string | null;
}

export interface CmmdWsTokenResult {
  readonly token: string;
  readonly wsUrl: string;
}

export interface CreateCmmdProjectParams {
  readonly name: string;
  readonly campaignId?: number | null;
  readonly organizationId: number;
}

export interface EnsureGitHubProjectContextParams {
  readonly githubRepoFullName: string;
  readonly projectName: string;
  readonly cmmdOrganizationId: number;
  readonly campaignId?: number | null;
}

export type { CmmdCodexCredentialState, CmmdClaudeCredentialState, CmmdProviderAccountInventory };

// ── Public interface ──────────────────────────────────────────────────────────

export interface CmmdApiClientInterface
  extends
    AuthRuntimeApi,
    ThreadDispositionApi,
    ThreadArchiveApi,
    ThreadAttachmentsApi,
    UserAccountApi {
  listCampaigns(params?: {
    orgId?: string;
    allWorkspaces?: boolean;
    signal?: AbortSignal;
  }): Promise<ReadonlyArray<CmmdCampaign>>;
  getCampaign(id: string): Promise<CmmdCampaign>;
  createCampaign(params: CreateCampaignParams): Promise<CmmdCampaign>;
  updateCampaign(id: string, patch: UpdateCampaignParams): Promise<CmmdCampaign>;
  deleteCampaign(id: string): Promise<void>;
  listProjects(params?: {
    campaignId?: string;
    orgId?: string;
    /**
     * Cross-workspace mode: return projects from every workspace the user
     * belongs to, each tagged with workspaceId/workspaceName. Used by the CMMD
     * Center so its Workspace column and filter span all workspaces in one call.
     */
    allWorkspaces?: boolean;
    signal?: AbortSignal;
  }): Promise<ReadonlyArray<CmmdProject>>;
  listTasks(params?: { projectId?: string; status?: string }): Promise<ReadonlyArray<CmmdTask>>;

  getTask(id: string): Promise<CmmdTask>;
  getForgeTaskDetail(taskId: number): Promise<CmmdForgeTaskDetail>;
  searchTasks(params: SearchTasksParams): Promise<ReadonlyArray<CmmdTask>>;
  getTaskComments(taskId: string): Promise<ReadonlyArray<CmmdComment>>;
  listProjectTaskComments(taskId: number): Promise<ReadonlyArray<CmmdTaskComment>>;
  createProjectTaskComment(
    taskId: number,
    params: CreateCmmdTaskCommentParams,
  ): Promise<CmmdTaskComment>;
  updateProjectTaskComment(
    taskId: number,
    commentKey: string,
    params: UpdateCmmdTaskCommentParams,
  ): Promise<CmmdTaskComment>;
  deleteProjectTaskComment(
    taskId: number,
    commentKey: string,
    params: DeleteCmmdTaskCommentParams,
  ): Promise<CmmdTaskCommentDeleteReceipt>;
  getTaskAttachments(taskId: string): Promise<ReadonlyArray<CmmdAttachment>>;
  getProject(id: string): Promise<CmmdProject>;
  getProjectRepository(id: string): Promise<CmmdProjectRepository>;
  listProjectRepositories(id: string): Promise<CmmdProjectRepositories>;
  getProjectMembers(projectId: string): Promise<ReadonlyArray<CmmdMember>>;
  listWorkspaceMembers(): Promise<ReadonlyArray<CmmdMember>>;
  addProjectMember(
    projectId: string,
    params: AddCmmdProjectMemberParams,
  ): Promise<AddCmmdProjectMemberResult>;
  listProjectAgentMembers(projectId: string): Promise<ReadonlyArray<CmmdProjectAgentMember>>;
  createProjectAgentMember(
    projectId: string,
    params: CreateCmmdProjectAgentMemberParams,
  ): Promise<CreateCmmdProjectAgentMemberResult>;
  renameProjectAgentMember(
    projectId: string,
    memberId: number,
    params: RenameCmmdProjectAgentMemberParams,
  ): Promise<RenameCmmdProjectAgentMemberResult>;
  retireProjectAgentMember(
    projectId: string,
    memberId: number,
    params: RetireCmmdProjectAgentMemberParams,
  ): Promise<RetireCmmdProjectAgentMemberResult>;
  updateProjectAgentMemberAvatar(
    projectId: string,
    memberId: number,
    params: UpdateCmmdProjectAgentMemberAvatarParams,
  ): Promise<UpdateCmmdProjectAgentMemberAvatarResult>;
  listProjectAgentMemberInstances(
    projectId: string,
    memberId: number,
  ): Promise<ReadonlyArray<CmmdProjectAgentInstance>>;
  previewProjectAgentMemberConsolidation(
    projectId: string,
  ): Promise<CmmdProjectAgentMemberConsolidationPreview>;
  applyProjectAgentMemberConsolidation(
    projectId: string,
    params: ApplyCmmdProjectAgentMemberConsolidationParams,
  ): Promise<CmmdProjectAgentMemberConsolidationResult>;
  setProjectTaskAssignment(
    projectId: string,
    taskId: number,
    params: CmmdProjectTaskAssignmentParams,
  ): Promise<CmmdProjectTaskAssignmentResult>;
  getProjectUpdates(
    projectId: string,
    params?: { readonly page?: number; readonly limit?: number },
  ): Promise<CmmdProjectUpdatesPage>;
  createProjectUpdate(
    projectId: string,
    input: { status: CmmdProjectUpdateStatus; body: string },
  ): Promise<CmmdProjectUpdateView>;
  updateProjectUpdate(
    projectId: string,
    updateId: string,
    input: { status: CmmdProjectUpdateStatus; body: string },
  ): Promise<CmmdProjectUpdateView>;
  deleteProjectUpdate(projectId: string, updateId: string): Promise<void>;
  listProjectMilestones(projectId: string): Promise<ReadonlyArray<CmmdProjectMilestone>>;
  createProjectMilestone(
    projectId: string,
    input: CreateCmmdProjectMilestoneParams,
  ): Promise<CmmdProjectMilestone>;
  updateProjectMilestone(
    projectId: string,
    milestoneId: number,
    input: UpdateCmmdProjectMilestoneParams,
  ): Promise<CmmdProjectMilestone>;
  deleteProjectMilestone(projectId: string, milestoneId: number): Promise<void>;
  improveProjectContent(
    projectId: string,
    input: { surface: CmmdProjectContentSurface; content: string },
  ): Promise<CmmdProjectContentReadabilityResult>;
  listProjectBoard(projectId: string): Promise<ReadonlyArray<CmmdBoardTask>>;
  createProjectTaskDraft(
    projectId: string,
    params: CreateCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraft>;
  getProjectTaskDraft(projectId: string, draftId: number): Promise<CmmdProjectTaskDraft>;
  updateProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: UpdateCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraft>;
  previewProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: PreviewCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraftPreview>;
  commitProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: CommitCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraftCommit>;
  discardProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: DiscardCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraft>;
  getProjectLeadPlan(projectId: string): Promise<CmmdProjectLeadPlan>;
  activateProjectLeadAutomaticStaffing(
    projectId: string,
  ): Promise<ActivateCmmdProjectLeadAuthorityResult>;
  getProjectLeadReadiness(projectId: string): Promise<CmmdProjectLeadReadiness>;
  getProjectLeadCapabilities(projectId: string): Promise<CmmdProjectLeadCapabilities>;
  getProjectLeadSummary(projectId: string): Promise<CmmdProjectLeadSummary>;
  getProjectLeadDetail(projectId: string): Promise<CmmdProjectLeadDetail>;
  getProjectLeadBriefing(projectId: string): Promise<CmmdProjectLeadBriefing>;
  decideProjectLeadApproval(
    projectId: string,
    approvalId: string,
    params: DecideCmmdProjectLeadApprovalParams,
  ): Promise<DecideCmmdProjectLeadApprovalResult>;
  listProjectLeadEvents(
    projectId: string,
    params?: CmmdProjectLeadEventsParams,
  ): Promise<CmmdProjectLeadEvents>;
  explainProjectLead(projectId: string): Promise<CmmdProjectLeadExplanation>;
  pauseProjectLead(
    projectId: string,
    params: PauseCmmdProjectLeadParams,
  ): Promise<PauseCmmdProjectLeadResult>;
  resumeProjectLead(
    projectId: string,
    params: ResumeCmmdProjectLeadParams,
  ): Promise<ResumeCmmdProjectLeadResult>;
  reserveProjectLeadTask(
    projectId: string,
    taskId: number,
    params: ReserveCmmdProjectLeadTaskParams,
  ): Promise<ReserveCmmdProjectLeadTaskResult>;
  releaseProjectLeadReservation(
    projectId: string,
    reservationId: number,
    params: ReleaseCmmdProjectLeadReservationParams,
  ): Promise<ReleaseCmmdProjectLeadReservationResult>;
  reconcileProjectLead(
    projectId: string,
    params: ReconcileCmmdProjectLeadParams,
  ): Promise<ReconcileCmmdProjectLeadResult>;
  resolveProjectLeadAttention(
    projectId: string,
    attentionId: number,
    params: ResolveCmmdProjectLeadAttentionParams,
  ): Promise<ResolveCmmdProjectLeadAttentionResult>;
  listCenterBoard(params?: ListCmmdCenterBoardParams): Promise<CmmdCenterBoardResponse>;
  getProjectTaskWorkflow(projectId: string): Promise<CmmdTaskWorkflow>;
  updateProjectTaskWorkflow(
    projectId: string,
    workflow: CmmdTaskWorkflow,
  ): Promise<CmmdTaskWorkflow>;
  listForgeAutomationPresets(): Promise<ReadonlyArray<CmmdForgeAutomationPreset>>;
  listUserForgeAutomationRules(): Promise<ReadonlyArray<CmmdUserForgeAutomationRule>>;
  createUserForgeAutomationRule(
    params: CmmdCreateUserForgeAutomationRuleParams,
  ): Promise<CmmdUserForgeAutomationRule>;
  updateUserForgeAutomationRule(
    ruleId: number,
    params: CmmdUpdateUserForgeAutomationRuleParams,
  ): Promise<CmmdUserForgeAutomationRule>;
  listUserForgeAutomationRuns(ruleId: number): Promise<ReadonlyArray<CmmdUserForgeAutomationRun>>;
  startUserForgeAutomationRun(
    ruleId: number,
    params: CmmdStartUserForgeAutomationRunParams,
  ): Promise<CmmdStartUserForgeAutomationRunResult>;
  listForgeAutomationRules(
    projectId: string,
    scope?: CmmdForgeAutomationScope,
  ): Promise<ReadonlyArray<CmmdForgeAutomationRule>>;
  createForgeAutomationRule(
    projectId: string,
    params: CmmdCreateForgeAutomationRuleParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdForgeAutomationRule>;
  updateForgeAutomationRule(
    projectId: string,
    ruleId: number,
    params: CmmdUpdateForgeAutomationRuleParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdForgeAutomationRule>;
  listForgeAutomationRuns(
    projectId: string,
    params?: CmmdForgeAutomationScope & { readonly taskId?: number },
  ): Promise<ReadonlyArray<CmmdForgeAutomationRun>>;
  getForgeAutomationRun(
    runId: number,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdForgeAutomationRunDetail>;
  startForgeAutomationRun(
    projectId: string,
    taskId: number,
    params: CmmdStartForgeAutomationRunParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdStartForgeAutomationRunResult>;
  retryForgeAutomationRun(
    runId: number,
    params: CmmdRetryForgeAutomationRunParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdRetryForgeAutomationRunResult>;
  cancelForgeAutomationRun(
    runId: number,
    params: CmmdCancelForgeAutomationRunParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdCancelForgeAutomationRunResult>;
  approveForgeAutomationEscalation(
    runId: number,
    params: CmmdApproveForgeAutomationEscalationParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdApproveForgeAutomationEscalationResult>;
  rejectForgeAutomationEscalation(
    runId: number,
    params: CmmdRejectForgeAutomationEscalationParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdRejectForgeAutomationEscalationResult>;
  createProjectBoardTask(
    projectId: string,
    params: CreateCmmdBoardTaskParams,
  ): Promise<CmmdBoardTask>;
  createCenterBoardTask(params: CreateCmmdCenterBoardTaskParams): Promise<CmmdBoardTask>;
  submitFeedback(params: CreateCmmdFeedbackParams): Promise<CmmdFeedbackIntakeResult>;
  updateProjectBoardTask(
    projectId: string,
    taskId: number,
    patch: UpdateCmmdBoardTaskParams,
  ): Promise<CmmdBoardTask>;
  updateCenterBoardTask(taskId: number, patch: UpdateCmmdBoardTaskParams): Promise<CmmdBoardTask>;
  moveCenterBoardTask(
    taskId: number,
    params: MoveCmmdCenterBoardTaskParams,
  ): Promise<CmmdBoardTask>;
  moveProjectBoardTask(
    projectId: string,
    taskId: number,
    params: MoveCmmdProjectBoardTaskParams,
  ): Promise<CmmdBoardTask>;
  archiveProjectBoardTask(projectId: string, taskId: number): Promise<CmmdBoardTask>;
  unarchiveProjectBoardTask(projectId: string, taskId: number): Promise<CmmdBoardTask>;
  updateProjectBoardTaskStatus(
    projectId: string,
    taskId: number,
    status: CmmdTaskStatus,
    workflowStageId?: string,
  ): Promise<CmmdBoardTask>;
  listTaskExecutionOptions(
    projectId: string,
    taskId: number,
  ): Promise<ReadonlyArray<CmmdTaskExecutionOption>>;
  createTaskWorkSession(
    projectId: string,
    taskId: number,
    params: CmmdCreateWorkSessionParams,
  ): Promise<CmmdCreateWorkSessionResult>;
  listProjectWorkSessions(
    projectId: string,
    params?: CmmdListWorkSessionsParams,
  ): Promise<ReadonlyArray<CmmdWorkSession>>;
  getWorkSession(workSessionId: string): Promise<CmmdWorkSession>;
  updateWorkSessionStatus(
    workSessionId: string,
    status: CmmdWorkSessionStatus,
  ): Promise<CmmdWorkSession>;
  attachWorkSessionThread(
    workSessionId: string,
    params: CmmdAttachWorkSessionThreadParams,
  ): Promise<CmmdAttachWorkSessionThreadResult>;
  listTaskConversations(
    taskId: number,
    params?: CmmdTaskConversationListParams,
  ): Promise<CmmdTaskConversationList>;
  linkTaskConversation(
    taskId: number,
    params: LinkCmmdTaskConversationParams,
  ): Promise<LinkCmmdTaskConversationResult>;
  getTaskHistory(taskId: number): Promise<CmmdTaskHistory>;
  upsertTaskPullRequest(
    taskId: number,
    params: UpsertCmmdTaskPullRequestParams,
  ): Promise<UpsertCmmdTaskPullRequestResult>;
  listPullRequests(input: CmmdPullRequestListInput): Promise<CmmdPullRequestListResult>;
  getPullRequestListStats(
    input: CmmdPullRequestListStatsInput,
  ): Promise<CmmdPullRequestListStatsResult>;
  getPullRequestDetail(input: CmmdPullRequestRef): Promise<CmmdPullRequestDetail>;
  getPullRequestActivity(input: CmmdPullRequestRef): Promise<CmmdPullRequestActivity>;
  getPullRequestDiff(input: CmmdPullRequestDiffInput): Promise<CmmdPullRequestDiffResult>;
  getPullRequestDiffFileContents(
    input: CmmdPullRequestDiffFileContentsInput,
  ): Promise<CmmdPullRequestDiffFileContentsResult>;
  getPullRequestThreadComments(
    input: CmmdPullRequestThreadCommentsInput,
  ): Promise<CmmdPullRequestThreadCommentsResult>;
  getPullRequestReviewerCandidates(
    input: CmmdPullRequestReviewerCandidatesInput,
  ): Promise<CmmdPullRequestReviewerCandidatesResult>;
  runPullRequestAction(input: CmmdPullRequestActionInput): Promise<CmmdPullRequestMutationResult>;
  createPullRequestComment(
    input: CmmdPullRequestCommentInput,
  ): Promise<CmmdPullRequestMutationResult>;
  updatePullRequest(input: CmmdPullRequestUpdateInput): Promise<CmmdPullRequestMutationResult>;
  updatePullRequestComment(
    input: CmmdPullRequestCommentUpdateInput,
  ): Promise<CmmdPullRequestMutationResult>;
  submitPullRequestReview(
    input: CmmdPullRequestReviewInput,
  ): Promise<CmmdPullRequestMutationResult>;
  replyToPullRequestThread(
    input: CmmdPullRequestThreadReplyInput,
  ): Promise<CmmdPullRequestMutationResult>;
  setPullRequestThreadResolution(
    input: CmmdPullRequestThreadResolutionInput,
  ): Promise<CmmdPullRequestMutationResult>;
  requestPullRequestReviewers(
    input: CmmdPullRequestReviewersInput,
  ): Promise<CmmdPullRequestMutationResult>;
  setPullRequestReaction(
    input: CmmdPullRequestReactionInput,
  ): Promise<CmmdPullRequestMutationResult>;
  getTaskConversation(
    taskId: number,
    conversationId: string,
    params?: CmmdTaskConversationParams,
  ): Promise<CmmdTaskConversation>;
  getWorkspaceMembers(workspaceId: string): Promise<ReadonlyArray<CmmdMember>>;
  getWebSocketToken(params?: { signal?: AbortSignal }): Promise<CmmdWsTokenResult>;
  getProjectComments(
    projectId: string,
    params?: { organizationId?: string | null },
  ): Promise<ReadonlyArray<CmmdComment>>;
  createProjectComment(
    projectId: string,
    params: { content: string; organizationId?: string | null },
  ): Promise<CmmdComment>;
  getProjectLink(params: {
    environmentId: string;
    projectId: string;
    cwd: string;
  }): Promise<ProjectLinkData>;
  listProjectLinks(): Promise<{
    readonly links: ReadonlyArray<{
      readonly forgeEnvironmentId: string;
      readonly forgeProjectId: string;
      readonly cmmdOrganizationId: number;
      readonly cmmdProjectId: number;
    }>;
    readonly sources?: {
      readonly cmmd: number;
      readonly cmmdAvailable?: boolean;
    };
  }>;
  listProjectContextLinks(params: {
    environmentId?: string;
    projectId?: string;
    githubRepoFullName?: string;
    cmmdOrganizationId?: number;
  }): Promise<ReadonlyArray<CmmdProjectContextLink>>;
  setProjectLink(body: Record<string, unknown>): Promise<void>;
  deleteProjectLink(params: {
    environmentId: string;
    projectId: string;
    cwd: string;
    cmmdProjectId?: number | null;
  }): Promise<void>;
  listCmmdWorkspaces(): Promise<ReadonlyArray<WellKnownItem>>;
  listCmmdCampaigns(params: { workspaceId: number }): Promise<ReadonlyArray<CmmdCampaignRow>>;
  listCmmdProjects(params: { campaignId: number }): Promise<ReadonlyArray<WellKnownItem>>;
  createCmmdProject(params: CreateCmmdProjectParams): Promise<WellKnownItem>;
  ensureGitHubProjectContext(
    params: EnsureGitHubProjectContextParams,
  ): Promise<CmmdEnsuredGitHubProjectContext>;

  updateTask(id: string, patch: UpdateTaskParams): Promise<{ success: boolean; details: unknown }>;
  createTask(params: CreateTaskParams): Promise<CmmdTask>;
  archiveTask(id: string): Promise<void>;
  unarchiveTask(id: string): Promise<void>;
  createSubtask(params: CreateSubtaskParams): Promise<CmmdTask>;
  createBoardTask(params: CreateBoardTaskParams): Promise<CmmdTask>;
  createProject(params: CreateProjectParams): Promise<CmmdProject>;
  updateProject(id: string, patch: UpdateProjectParams): Promise<void>;
  createComment(params: CreateCommentParams): Promise<{ success: boolean; details: unknown }>;
  updateComment(taskId: string, commentId: string, content: string): Promise<{ success: boolean }>;
  deleteComment(taskId: string, commentId: string): Promise<void>;
  uploadTaskAttachment(taskId: string, file: File): Promise<CmmdAttachment>;
  uploadBrainDoc(file: File, meta: BrainDocMeta): Promise<CmmdBrainDoc>;
  createBrainDoc(params: CreateBrainDocParams): Promise<CmmdBrainDoc>;

  moveProject(
    projectId: string,
    params: {
      targetOrgId: string;
      campaignStrategy?: "detach" | "reassign";
      targetCampaignId?: string;
      dryRun?: boolean;
    },
  ): Promise<CmmdMoveReport>;

  // ── Bootstrap ────────────────────────────────────────────────────────────────
  getProfile(): Promise<CmmdProfileData>;
  getUsageSummary(input: CmmdUsageSummaryInput): Promise<UsageSummary>;
  getBilling(input?: BillingReadInput): Promise<CmmdBillingContext>;
  getBillingSeries(input: { timeZone: string }): Promise<CmmdBillingSeries>;
  getBootstrap(input?: BootstrapReadInput): Promise<CmmdBootstrapData>;
  getOrgProviderPolicy(): Promise<CmmdOrgProviderPolicy>;
  listSavedDataViews(params: ListCmmdSavedDataViewsParams): Promise<readonly CmmdSavedDataView[]>;
  createSavedDataView(params: CreateCmmdSavedDataViewParams): Promise<CmmdSavedDataView>;
  importSavedDataView(
    params: ImportCmmdSavedDataViewParams,
  ): Promise<ImportCmmdSavedDataViewResult>;
  updateSavedDataView(
    viewId: string,
    params: UpdateCmmdSavedDataViewParams,
  ): Promise<CmmdSavedDataView>;
  deleteSavedDataView(
    viewId: string,
    params: DeleteCmmdSavedDataViewParams,
  ): Promise<CmmdSavedDataView>;
  getDeviceRegistry(params?: {
    readonly projectId?: number;
    readonly signal?: AbortSignal;
  }): Promise<CmmdDeviceRegistrySnapshot>;
  registerDevice(params: RegisterCmmdDeviceParams): Promise<CmmdDeviceRegistrySnapshot>;
  grantRuntimeAccess(
    runtimeId: string,
    params: GrantCmmdRuntimeAccessParams,
  ): Promise<CmmdRuntimeAccessGrant>;
  revokeRuntimeAccess(runtimeId: string, grantId: number): Promise<CmmdRuntimeAccessRevokeResult>;
  issueRelayControlRoute(
    runtimeId: string,
    params: CreateCmmdRelayControlRouteParams,
  ): Promise<CmmdRelayControlRoute>;
  issueRelayActionGrant(
    runtimeId: string,
    params: CreateCmmdRelayActionGrantParams,
  ): Promise<CmmdRelayActionGrant>;
  getRuntimeInventory(
    params?: GetCmmdRuntimeInventoryParams,
  ): Promise<CmmdRuntimeInventorySnapshot>;
  getRuntimeDiagnostics(runtimeId: string): Promise<CmmdRuntimeDiagnosticsSnapshot>;
  getCloudProjectLifecyclePreflight(projectId: number): Promise<CmmdCloudProjectLifecyclePreflight>;
  replaceRuntimeInventory(
    runtimeId: string,
    params: ReplaceCmmdRuntimeInventoryParams,
  ): Promise<CmmdRuntimeInventoryRuntime>;
  /**
   * Card 1359 — ephemeral personal-provider sign-in runtime (cloud-only users).
   * Owner-authenticated; the whole resource is 404 unless the CMMD feature flag
   * is on. Provision/get/destroy return the non-secret runtime state; connect
   * returns a transport-scoped descriptor for opening a relay connection to the
   * auth VM.
   */
  getUserCodexCredential(): Promise<CmmdCodexCredentialState>;
  disconnectUserCodexCredential(): Promise<CmmdCodexCredentialState>;
  getUserClaudeCredential(): Promise<CmmdClaudeCredentialState>;
  disconnectUserClaudeCredential(): Promise<CmmdClaudeCredentialState>;
  getProviderAccounts(): Promise<CmmdProviderAccountInventory>;
  removeLinkedProviderAccount(id: string | number): Promise<{ readonly removed: boolean }>;
  setProviderAccountDefault(params: {
    readonly provider: "codex" | "claudeAgent";
    readonly providerAccountId: string;
  }): Promise<CmmdProviderAccountInventory>;
  getProviderUsage(): Promise<CmmdProviderUsageSnapshots>;
  refreshProviderUsage(
    provider: "codex" | "claude",
    providerAccountId: string,
  ): Promise<CmmdProviderUsageSnapshots>;
  getClaudeRuntimeReadiness(runtimeId: string): Promise<CmmdClaudeRuntimeReadiness>;
  appendRuntimeThreadEvents(
    runtimeId: string,
    threadId: string,
    params: AppendCmmdRuntimeThreadEventsParams,
  ): Promise<AppendCmmdRuntimeThreadEventsResult>;
  getRuntimeThreadReplica(runtimeId: string, threadId: string): Promise<CmmdRuntimeThreadReplica>;
  renameCloudEnvironment(
    runtimeId: string,
    displayName: string | null,
  ): Promise<CmmdRenamedForgeCloudEnvironment>;
  renameDevice(deviceId: number, displayName: string): Promise<CmmdDeviceRegistryDevice>;
  revokeDevice(deviceId: number): Promise<CmmdDeviceRegistryDevice>;
  listSimHosts(params?: CmmdSimScopeParams): Promise<ReadonlyArray<CmmdSimHost>>;
  getSimLease(params: CmmdSimLeaseLookupParams): Promise<CmmdSimLease | null>;
  claimSimLease(params: CmmdSimLeaseClaimParams): Promise<CmmdSimLease>;
  renewSimLease(params: CmmdSimLeaseFenceParams & { ttlMs: number }): Promise<CmmdSimLease>;
  releaseSimLease(params: CmmdSimLeaseFenceParams): Promise<void>;
}

// ── Config ────────────────────────────────────────────────────────────────────

// ── Simulator hosts and leases ────────────────────────────────────────────────
//
// Mirrors CMMD's owner lane. `generation` and `rowVersion` are carried on every
// lease because release and renew are fenced on them — a client that drops them
// holds a simulator it can see and cannot hand back.

/** Forge is cross-workspace, so a caller may need to name the workspace. */
export interface CmmdSimScopeParams {
  readonly orgId?: number;
}

export interface CmmdSimHost {
  readonly id: number;
  readonly hostKey: string;
  readonly maxConcurrentLeases: number;
  readonly liveLeases: number;
}

export interface CmmdSimLease {
  readonly id: number;
  readonly generation: number;
  readonly rowVersion: number;
  readonly state: "allocating" | "active" | "releasing" | "released" | "blocked";
  readonly hostId: number;
  readonly expiresAt: string;
}

export interface CmmdSimLeaseLookupParams extends CmmdSimScopeParams {
  readonly forgeEnvironmentId: string;
  readonly threadId: string;
}

export interface CmmdSimLeaseClaimParams extends CmmdSimLeaseLookupParams {
  readonly hostId: number;
  readonly ttlMs: number;
}

/** The fence a caller must return to write to a lease it already read. */
export interface CmmdSimLeaseFenceParams extends CmmdSimScopeParams {
  readonly leaseId: number;
  readonly generation: number;
  readonly rowVersion: number;
}

export interface CmmdApiClientConfig {
  readonly forgeOrigin?: string;
  readonly maxRetries?: number;
  readonly onAuthExpired?: (status?: number) => void | Promise<void>;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function extractWellKnownItems(data: unknown): ReadonlyArray<WellKnownItem> {
  if (Array.isArray(data)) return data as WellKnownItem[];
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj["items"])) return obj["items"] as WellKnownItem[];
    if (Array.isArray(obj["results"])) return obj["results"] as WellKnownItem[];
  }
  return [];
}

function extractCampaignRows(data: unknown): ReadonlyArray<CmmdCampaignRow> {
  return extractWellKnownItems(data).flatMap((raw) => {
    const item = raw as unknown as Record<string, unknown>;
    const id = typeof item["id"] === "number" ? (item["id"] as number) : null;
    const name = typeof item["name"] === "string" ? (item["name"] as string) : null;
    if (id === null || name === null) return [];
    const str = (key: string): string | null =>
      typeof item[key] === "string" ? (item[key] as string) : null;
    return [
      {
        id,
        name,
        startDate: str("startDate"),
        endDate: str("endDate"),
        status: str("status"),
      },
    ];
  });
}

function unwrapProjectMutationResponse(data: unknown): CmmdProject {
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (obj["project"] && typeof obj["project"] === "object") {
      return obj["project"] as CmmdProject;
    }
  }
  return data as CmmdProject;
}

function toCmmdMutationId(value: string | null): number | string | null {
  if (value === null || value.trim() === "") return null;
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) ? numeric : value;
}

// ── Implementation ────────────────────────────────────────────────────────────

class CmmdApiClient implements CmmdApiClientInterface {
  private readonly cfg: TransportConfig;
  readonly putThreadDisposition: ThreadDispositionApi["putThreadDisposition"];
  readonly listThreadAttachments: ThreadAttachmentsApi["listThreadAttachments"];
  readonly uploadThreadAttachment: ThreadAttachmentsApi["uploadThreadAttachment"];
  readonly getThreadAttachmentDownloadUrl: ThreadAttachmentsApi["getThreadAttachmentDownloadUrl"];
  readonly deleteThreadAttachment: ThreadAttachmentsApi["deleteThreadAttachment"];
  readonly postThreadArchive: ThreadArchiveApi["postThreadArchive"];
  readonly getForgeAccountPreferences: UserAccountApi["getForgeAccountPreferences"];
  readonly updateForgeAccountPreferences: UserAccountApi["updateForgeAccountPreferences"];
  readonly getUserSettings: UserAccountApi["getUserSettings"];
  readonly putUserSettings: UserAccountApi["putUserSettings"];
  private readonly commentEditIdempotencyKeys = new Map<string, string>();
  private readonly commentDeleteIdempotencyKeys = new Map<string, string>();

  constructor(config: CmmdApiClientConfig = {}) {
    const usePrimaryEnvironment = config.forgeOrigin === undefined;
    const origin = config.forgeOrigin ?? "http://localhost:13773";
    this.cfg = {
      resolveUrl: usePrimaryEnvironment
        ? resolvePrimaryEnvironmentHttpUrl
        : (path) => `${origin}${path}`,
      maxRetries: config.maxRetries ?? 3,
      // Default to product logout+login on CMMD REST 401 so bare createCmmdApiClient()
      // call sites (preferences, projects, setup) cannot strand users on fatal screens.
      onAuthExpired: config.onAuthExpired ?? handleCmmdAuthExpired,
      ...(usePrimaryEnvironment
        ? {
            resolveBearerToken: () => readPrimaryEnvironmentTarget()?.bearerToken ?? null,
          }
        : {}),
    };
    this.putThreadDisposition = bindThreadDispositionApi(this.cfg);
    const threadAttachmentsApi = bindThreadAttachmentsApi(this.cfg);
    this.listThreadAttachments = threadAttachmentsApi.listThreadAttachments;
    this.uploadThreadAttachment = threadAttachmentsApi.uploadThreadAttachment;
    this.getThreadAttachmentDownloadUrl = threadAttachmentsApi.getThreadAttachmentDownloadUrl;
    this.deleteThreadAttachment = threadAttachmentsApi.deleteThreadAttachment;
    this.postThreadArchive = bindThreadArchiveApi(this.cfg);
    const userAccount = bindUserAccountApi(this.cfg);
    this.getForgeAccountPreferences = userAccount.getForgeAccountPreferences;
    this.updateForgeAccountPreferences = userAccount.updateForgeAccountPreferences;
    this.getUserSettings = userAccount.getUserSettings;
    this.putUserSettings = userAccount.putUserSettings;
  }

  // ── Implemented reads ───────────────────────────────────────────────────────

  async getTask(id: string): Promise<CmmdTask> {
    return legacyRequest(
      request<CmmdTask>({ method: "GET", path: `/api/cmmd/tasks/${id}` }, this.cfg),
    );
  }

  async getForgeTaskDetail(taskId: number): Promise<CmmdForgeTaskDetail> {
    return request<CmmdForgeTaskDetail>(
      { method: "GET", path: `/api/cmmd/tasks/${taskId}` },
      this.cfg,
    );
  }

  async searchTasks(params: SearchTasksParams): Promise<ReadonlyArray<CmmdTask>> {
    const query: Record<string, string | number | undefined> = { q: params.q };
    if (params.status) query["status"] = params.status;
    if (params.limit !== undefined) query["limit"] = params.limit;
    if (params.projectId) query["projectId"] = params.projectId;
    const result = await legacyRequest(
      request<{ tasks?: ReadonlyArray<CmmdTask> }>(
        { method: "GET", path: "/api/cmmd/tasks/search", query },
        this.cfg,
      ),
    );
    return result.tasks ?? [];
  }

  async getTaskComments(taskId: string): Promise<ReadonlyArray<CmmdComment>> {
    // Server normalizes to { comments: [...] } — not yet registry-migrated
    const result = await legacyRequest(
      request<{ comments?: ReadonlyArray<CmmdComment> }>(
        { method: "GET", path: `/api/cmmd/tasks/${taskId}/comments` },
        this.cfg,
      ),
    );
    return result.comments ?? [];
  }

  async listProjectTaskComments(taskId: number): Promise<ReadonlyArray<CmmdTaskComment>> {
    const result = await legacyRequest(
      request<{ comments?: ReadonlyArray<CmmdTaskComment> }>(
        { method: "GET", path: `/api/cmmd/tasks/${taskId}/comments` },
        this.cfg,
      ),
    );
    return result.comments ?? [];
  }

  async createProjectTaskComment(
    taskId: number,
    params: CreateCmmdTaskCommentParams,
  ): Promise<CmmdTaskComment> {
    const result = await legacyRequest(
      request<{ success: boolean; details: CmmdTaskComment }>(
        {
          method: "POST",
          path: "/api/cmmd/tasks/comment",
          body: { taskId, content: params.content },
        },
        this.cfg,
      ),
    );
    return result.details;
  }

  async updateProjectTaskComment(
    taskId: number,
    commentKey: string,
    params: UpdateCmmdTaskCommentParams,
  ): Promise<CmmdTaskComment> {
    const result = await legacyRequest(
      request<{ comment: CmmdTaskComment; replayed: boolean }>(
        {
          method: "PATCH",
          path: `/api/cmmd/tasks/${taskId}/comments/${encodeURIComponent(commentKey)}`,
          body: params,
        },
        this.cfg,
      ),
    );
    return result.comment;
  }

  async deleteProjectTaskComment(
    taskId: number,
    commentKey: string,
    params: DeleteCmmdTaskCommentParams,
  ): Promise<CmmdTaskCommentDeleteReceipt> {
    return legacyRequest(
      request<CmmdTaskCommentDeleteReceipt>(
        {
          method: "DELETE",
          path: `/api/cmmd/tasks/${taskId}/comments/${encodeURIComponent(commentKey)}`,
          body: params,
        },
        this.cfg,
      ),
    );
  }

  async getTaskAttachments(taskId: string): Promise<ReadonlyArray<CmmdAttachment>> {
    // Registry route (unwrapData) → server strips CMMD envelope → array
    return legacyRequest(
      request<ReadonlyArray<CmmdAttachment>>(
        { method: "GET", path: `/api/cmmd/tasks/${taskId}/attachments` },
        this.cfg,
      ),
    );
  }

  async getProjectRepository(id: string): Promise<CmmdProjectRepository> {
    // Registry route (unwrapData) → server strips CMMD envelope
    return legacyRequest(
      request<CmmdProjectRepository>(
        { method: "GET", path: `/api/cmmd/projects/${id}/repository` },
        this.cfg,
      ),
    );
  }

  async listProjectRepositories(id: string): Promise<CmmdProjectRepositories> {
    const result = await legacyRequest(
      request<{ readonly repositories: CmmdProjectRepositories }>(
        { method: "GET", path: `/api/cmmd/projects/${id}/repositories` },
        this.cfg,
      ),
    );
    return result.repositories;
  }

  async getWorkspaceMembers(workspaceId: string): Promise<ReadonlyArray<CmmdMember>> {
    // Registry route (unwrapData) → server strips CMMD envelope → array
    return legacyRequest(
      request<ReadonlyArray<CmmdMember>>(
        { method: "GET", path: `/api/cmmd/organizations/${workspaceId}/members` },
        this.cfg,
      ),
    );
  }

  async getWebSocketToken(params?: { signal?: AbortSignal }): Promise<CmmdWsTokenResult> {
    return request<CmmdWsTokenResult>(
      {
        method: "GET",
        path: "/api/cmmd/ws-token",
        ...(params?.signal !== undefined ? { signal: params.signal } : {}),
      },
      this.cfg,
    );
  }

  async getProjectComments(
    projectId: string,
    params?: { organizationId?: string | null },
  ): Promise<ReadonlyArray<CmmdComment>> {
    const query: Record<string, string | number | undefined> = {};
    if (params?.organizationId) query["organizationId"] = params.organizationId;
    const result = await request<{ comments?: ReadonlyArray<CmmdComment> }>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/comments`, query },
      this.cfg,
    );
    return result?.comments ?? [];
  }

  async getProjectLink(params: {
    environmentId: string;
    projectId: string;
    cwd: string;
  }): Promise<ProjectLinkData> {
    return request<ProjectLinkData>(
      {
        method: "GET",
        path: "/.well-known/forge/project-link",
        query: {
          environmentId: params.environmentId,
          projectId: params.projectId,
          cwd: params.cwd,
        },
      },
      this.cfg,
    );
  }

  async listProjectLinks(): Promise<{
    readonly links: ReadonlyArray<{
      readonly forgeEnvironmentId: string;
      readonly forgeProjectId: string;
      readonly cmmdOrganizationId: number;
      readonly cmmdProjectId: number;
    }>;
    readonly sources?: {
      readonly cmmd: number;
      readonly cmmdAvailable?: boolean;
    };
  }> {
    return request(
      { method: "GET", path: "/.well-known/forge/project-links", suppressBearerToken: true },
      this.cfg,
    );
  }

  async listProjectContextLinks(params: {
    environmentId?: string;
    projectId?: string;
    githubRepoFullName?: string;
    cmmdOrganizationId?: number;
  }): Promise<ReadonlyArray<CmmdProjectContextLink>> {
    const query: Record<string, string | number | undefined> = {};
    if (params.environmentId) query["forgeEnvironmentId"] = params.environmentId;
    if (params.projectId) query["forgeProjectId"] = params.projectId;
    if (params.githubRepoFullName) query["githubRepoFullName"] = params.githubRepoFullName;
    if (params.cmmdOrganizationId) query["cmmdOrganizationId"] = params.cmmdOrganizationId;
    const data = await request<unknown>(
      {
        method: "GET",
        path: "/.well-known/forge/cmmd/project-links",
        query,
      },
      this.cfg,
    );
    const decoded = Schema.decodeUnknownOption(CmmdProjectContextLinksResponseSchema)(data);
    return decoded._tag === "Some" ? decoded.value.links : [];
  }

  async listCmmdWorkspaces(): Promise<ReadonlyArray<WellKnownItem>> {
    const data = await request<unknown>(
      { method: "GET", path: "/.well-known/forge/cmmd/workspaces" },
      this.cfg,
    );
    return extractWellKnownItems(data);
  }

  async listCmmdCampaigns(params: {
    workspaceId: number;
  }): Promise<ReadonlyArray<CmmdCampaignRow>> {
    const data = await request<unknown>(
      {
        method: "GET",
        path: "/.well-known/forge/cmmd/campaigns",
        query: { workspaceId: params.workspaceId },
      },
      this.cfg,
    );
    return extractCampaignRows(data);
  }

  async listCmmdProjects(params: { campaignId: number }): Promise<ReadonlyArray<WellKnownItem>> {
    const data = await request<unknown>(
      {
        method: "GET",
        path: "/.well-known/forge/cmmd/projects",
        query: { campaignId: params.campaignId },
      },
      this.cfg,
    );
    return extractWellKnownItems(data);
  }

  // ── Implemented writes ──────────────────────────────────────────────────────

  async updateTask(
    id: string,
    patch: UpdateTaskParams,
  ): Promise<{ success: boolean; details: unknown }> {
    const body: Record<string, unknown> = { taskId: id };
    if (patch.title !== undefined) body["title"] = patch.title;
    if (patch.description !== undefined) body["description"] = patch.description;
    if (patch.status !== undefined) body["status"] = patch.status;
    if (patch.priority !== undefined) body["priority"] = patch.priority;
    if (patch.dueDate !== undefined) body["dueDate"] = patch.dueDate;
    if (patch.assigneeId !== undefined) body["assigneeId"] = patch.assigneeId;
    if (patch.labels !== undefined) body["labels"] = patch.labels;
    return legacyRequest(
      request<{ success: boolean; details: unknown }>(
        { method: "POST", path: "/api/cmmd/tasks/update", body },
        this.cfg,
      ),
    );
  }

  async createComment(
    params: CreateCommentParams,
  ): Promise<{ success: boolean; details: unknown }> {
    return legacyRequest(
      request<{ success: boolean; details: unknown }>(
        {
          method: "POST",
          path: "/api/cmmd/tasks/comment",
          body: { taskId: params.entityId, content: params.content },
        },
        this.cfg,
      ),
    );
  }

  async updateComment(
    taskId: string,
    commentId: string,
    content: string,
  ): Promise<{ success: boolean }> {
    const operationKey = `${taskId}:${commentId}:${content}`;
    const idempotencyKey = this.commentEditIdempotencyKeys.get(operationKey) ?? crypto.randomUUID();
    this.commentEditIdempotencyKeys.set(operationKey, idempotencyKey);
    const result = await legacyRequest(
      request<{ success: boolean }>(
        {
          method: "PATCH",
          path: `/api/cmmd/tasks/${taskId}/comments/${commentId}`,
          body: { content, idempotencyKey },
        },
        this.cfg,
      ),
    );
    this.commentEditIdempotencyKeys.delete(operationKey);
    return result;
  }

  async deleteComment(taskId: string, commentId: string): Promise<void> {
    const operationKey = `${taskId}:${commentId}`;
    const idempotencyKey =
      this.commentDeleteIdempotencyKeys.get(operationKey) ?? crypto.randomUUID();
    this.commentDeleteIdempotencyKeys.set(operationKey, idempotencyKey);
    await legacyRequest(
      request<void>(
        {
          method: "DELETE",
          path: `/api/cmmd/tasks/${taskId}/comments/${commentId}`,
          body: { idempotencyKey },
        },
        this.cfg,
      ),
    );
    this.commentDeleteIdempotencyKeys.delete(operationKey);
  }

  async createProjectComment(
    projectId: string,
    params: { content: string; organizationId?: string | null },
  ): Promise<CmmdComment> {
    const result = await request<{ comment?: CmmdComment }>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/comments`,
        body: { content: params.content, organizationId: params.organizationId ?? null },
      },
      this.cfg,
    );
    const comment = result?.comment;
    if (!comment) {
      throw new CmmdApiError({
        kind: "server",
        message: "CMMD did not return the created project comment.",
      });
    }
    return comment;
  }

  async updateProject(projectId: string, patch: UpdateProjectParams): Promise<void> {
    // Iterates the shared field list instead of restating it. The old if-chain
    // was the layer that silently dropped `icon`: the field existed in the UI
    // and in the contract, but no line here copied it onto the wire.
    const body: Record<string, unknown> = {};
    for (const field of CMMD_PROJECT_PATCH_FIELD_NAMES) {
      const value = patch[field];
      if (value === undefined) continue;
      // CMMD ids cross the wire as numbers where they are numeric; the client
      // carries them as strings, so they are converted at this one point.
      body[field] =
        CMMD_PROJECT_PATCH_FIELDS[field] === "nullableId" ? toCmmdMutationId(value) : value;
    }
    await request<unknown>(
      { method: "PATCH", path: `/api/cmmd/projects/${projectId}`, body },
      this.cfg,
    );
  }

  async setProjectLink(body: Record<string, unknown>): Promise<void> {
    await request<unknown>(
      { method: "POST", path: "/.well-known/forge/project-link", body },
      this.cfg,
    );
  }

  async deleteProjectLink(params: {
    environmentId: string;
    projectId: string;
    cwd: string;
    cmmdProjectId?: number | null;
  }): Promise<void> {
    await request<void>(
      {
        method: "DELETE",
        path: "/.well-known/forge/project-link",
        query: {
          environmentId: params.environmentId,
          projectId: params.projectId,
          cwd: params.cwd,
          ...(params.cmmdProjectId ? { cmmdProjectId: params.cmmdProjectId } : {}),
        },
      },
      this.cfg,
    );
  }

  async createCmmdProject(params: CreateCmmdProjectParams): Promise<WellKnownItem> {
    return request<WellKnownItem>(
      { method: "POST", path: "/.well-known/forge/cmmd/projects", body: params },
      this.cfg,
    );
  }

  async ensureGitHubProjectContext(
    params: EnsureGitHubProjectContextParams,
  ): Promise<CmmdEnsuredGitHubProjectContext> {
    return request<CmmdEnsuredGitHubProjectContext>(
      {
        method: "POST",
        path: "/.well-known/forge/cmmd/github-project-contexts/ensure",
        body: params,
      },
      this.cfg,
    );
  }

  async createBrainDoc(params: CreateBrainDocParams): Promise<CmmdBrainDoc> {
    return request<CmmdBrainDoc>(
      { method: "POST", path: "/api/cmmd/brain-docs", body: params },
      this.cfg,
    );
  }

  async uploadTaskAttachment(taskId: string, file: File): Promise<CmmdAttachment> {
    const form = new FormData();
    form.append("file", file);
    return legacyRequest(
      request<CmmdAttachment>(
        { method: "POST", path: `/api/cmmd/tasks/${taskId}/attachments`, form },
        this.cfg,
      ),
    );
  }

  async archiveTask(id: string): Promise<void> {
    await request<void>({ method: "POST", path: `/api/cmmd/tasks/${id}/archive` }, this.cfg);
  }

  async unarchiveTask(id: string): Promise<void> {
    await request<void>({ method: "POST", path: `/api/cmmd/tasks/${id}/unarchive` }, this.cfg);
  }

  async getProfile(): Promise<CmmdProfileData> {
    return request<CmmdProfileData>({ method: "GET", path: "/api/cmmd/profile" }, this.cfg);
  }

  /**
   * CMMD's own AI usage for the signed-in user, shaped like a Forge usage
   * source so the usage page can merge it with the transcript scans its
   * connected environments report.
   */
  async getUsageSummary(input: CmmdUsageSummaryInput): Promise<UsageSummary> {
    return request<UsageSummary>(
      {
        method: "GET",
        path: "/api/cmmd/usage/summary",
        query: {
          sinceDay: input.sinceDay,
          untilDay: input.untilDay,
          timeZone: input.timeZone,
          // Forwarded, not dropped: without the resolution and its bounds CMMD
          // can only answer in days, and the merge discards a day-only bucket
          // from its hourly map — so CMMD read as zero in the past-24h view
          // while still being counted in the totals above it.
          ...(input.resolution === "hour"
            ? {
                resolution: input.resolution,
                sinceTime: input.sinceTime,
                untilTime: input.untilTime,
              }
            : {}),
        },
      },
      this.cfg,
    );
  }

  getBilling(input?: BillingReadInput): Promise<CmmdBillingContext> {
    return fetchBilling(this.cfg, input);
  }

  getBillingSeries(input: { timeZone: string }): Promise<CmmdBillingSeries> {
    return fetchBillingSeries(this.cfg, input);
  }

  getBootstrap(input?: BootstrapReadInput): Promise<CmmdBootstrapData> {
    return fetchBootstrap(this.cfg, input);
  }

  async getOrgProviderPolicy(): Promise<CmmdOrgProviderPolicy> {
    return request<CmmdOrgProviderPolicy>(
      { method: "GET", path: "/api/cmmd/org/provider-policy" },
      this.cfg,
    );
  }

  async listSavedDataViews(
    params: ListCmmdSavedDataViewsParams,
  ): Promise<readonly CmmdSavedDataView[]> {
    return request<readonly CmmdSavedDataView[]>(
      {
        method: "GET",
        path: "/api/cmmd/saved-views",
        query: {
          surface: params.surface,
          projectId: params.projectId,
        },
      },
      this.cfg,
    );
  }

  async createSavedDataView(params: CreateCmmdSavedDataViewParams): Promise<CmmdSavedDataView> {
    return request<CmmdSavedDataView>(
      { method: "POST", path: "/api/cmmd/saved-views", body: params },
      this.cfg,
    );
  }

  async importSavedDataView(
    params: ImportCmmdSavedDataViewParams,
  ): Promise<ImportCmmdSavedDataViewResult> {
    return request<ImportCmmdSavedDataViewResult>(
      { method: "POST", path: "/api/cmmd/saved-views/import", body: params },
      this.cfg,
    );
  }

  async updateSavedDataView(
    viewId: string,
    params: UpdateCmmdSavedDataViewParams,
  ): Promise<CmmdSavedDataView> {
    return request<CmmdSavedDataView>(
      {
        method: "PATCH",
        path: `/api/cmmd/saved-views/${encodeURIComponent(viewId)}`,
        body: params,
      },
      this.cfg,
    );
  }

  async deleteSavedDataView(
    viewId: string,
    params: DeleteCmmdSavedDataViewParams,
  ): Promise<CmmdSavedDataView> {
    return request<CmmdSavedDataView>(
      {
        method: "DELETE",
        path: `/api/cmmd/saved-views/${encodeURIComponent(viewId)}`,
        query: params,
      },
      this.cfg,
    );
  }

  async getDeviceRegistry(params?: {
    readonly projectId?: number;
    readonly signal?: AbortSignal;
  }): Promise<CmmdDeviceRegistrySnapshot> {
    return request<CmmdDeviceRegistrySnapshot>(
      {
        method: "GET",
        path: "/api/cmmd/devices",
        ...(params?.projectId !== undefined
          ? { query: { projectId: String(params.projectId) } }
          : {}),
        ...(params?.signal !== undefined ? { signal: params.signal } : {}),
      },
      this.cfg,
    );
  }

  async registerDevice(params: RegisterCmmdDeviceParams): Promise<CmmdDeviceRegistrySnapshot> {
    return request<CmmdDeviceRegistrySnapshot>(
      { method: "POST", path: "/api/cmmd/devices/register", body: params },
      this.cfg,
    );
  }

  async grantRuntimeAccess(
    runtimeId: string,
    params: GrantCmmdRuntimeAccessParams,
  ): Promise<CmmdRuntimeAccessGrant> {
    return request<CmmdRuntimeAccessGrant>(
      {
        method: "POST",
        path: `/api/cmmd/devices/runtimes/${encodeURIComponent(runtimeId)}/access-grants`,
        body: params,
      },
      this.cfg,
    );
  }

  async revokeRuntimeAccess(
    runtimeId: string,
    grantId: number,
  ): Promise<CmmdRuntimeAccessRevokeResult> {
    return request<CmmdRuntimeAccessRevokeResult>(
      {
        method: "POST",
        path: `/api/cmmd/devices/runtimes/${encodeURIComponent(runtimeId)}/access-grants/${encodeURIComponent(String(grantId))}/revoke`,
      },
      this.cfg,
    );
  }

  async issueRelayControlRoute(
    runtimeId: string,
    params: CreateCmmdRelayControlRouteParams,
  ): Promise<CmmdRelayControlRoute> {
    return request<CmmdRelayControlRoute>(
      {
        method: "POST",
        path: `/api/cmmd/devices/runtimes/${encodeURIComponent(runtimeId)}/relay-routes`,
        body: params,
      },
      this.cfg,
    );
  }

  async issueRelayActionGrant(
    runtimeId: string,
    params: CreateCmmdRelayActionGrantParams,
  ): Promise<CmmdRelayActionGrant> {
    return request<CmmdRelayActionGrant>(
      {
        method: "POST",
        path: `/api/cmmd/devices/runtimes/${encodeURIComponent(runtimeId)}/relay-action-grants`,
        body: params,
      },
      this.cfg,
    );
  }

  async getRuntimeInventory(
    params?: GetCmmdRuntimeInventoryParams,
  ): Promise<CmmdRuntimeInventorySnapshot> {
    const query = buildRuntimeInventoryQuery(params);
    return withEmbedThreadViewerExampleInventory(
      await request<CmmdRuntimeInventorySnapshot>(
        {
          method: "GET",
          path: "/api/cmmd/runtime-inventory",
          ...(Object.keys(query).length > 0 ? { query } : {}),
        },
        this.cfg,
      ),
    );
  }

  async getRuntimeDiagnostics(runtimeId: string): Promise<CmmdRuntimeDiagnosticsSnapshot> {
    return request<CmmdRuntimeDiagnosticsSnapshot>(
      {
        method: "GET",
        path: `/api/cmmd/runtime-diagnostics/v2/runtimes/${encodeURIComponent(runtimeId)}`,
      },
      this.cfg,
    );
  }

  async getCloudProjectLifecyclePreflight(
    projectId: number,
  ): Promise<CmmdCloudProjectLifecyclePreflight> {
    const response = await request<CmmdCloudProjectLifecyclePreflightResponse>(
      {
        method: "GET",
        path: `/api/cmmd/forge-cloud-projects/${encodeURIComponent(String(projectId))}/lifecycle-preflight`,
      },
      this.cfg,
    );
    return response.preflight;
  }

  async replaceRuntimeInventory(
    runtimeId: string,
    params: ReplaceCmmdRuntimeInventoryParams,
  ): Promise<CmmdRuntimeInventoryRuntime> {
    return request<CmmdRuntimeInventoryRuntime>(
      {
        method: "PUT",
        path: `/api/cmmd/devices/runtimes/${encodeURIComponent(runtimeId)}/inventory`,
        body: params,
      },
      this.cfg,
    );
  }

  private static readonly PROVIDER_ACCOUNTS_PATH = "/api/cmmd/providers/accounts";
  private static readonly PROVIDER_USAGE_PATH = "/api/cmmd/providers/usage";
  private static readonly PROVIDER_USAGE_REFRESH_PATH = "/api/cmmd/providers/usage/refresh";

  provisionAuthRuntime(): Promise<CmmdAuthRuntimeState> {
    return provisionAuthRuntime(this.cfg);
  }

  getAuthRuntimeState(): Promise<CmmdAuthRuntimeState> {
    return getAuthRuntimeState(this.cfg);
  }

  destroyAuthRuntime(): Promise<CmmdAuthRuntimeState> {
    return destroyAuthRuntime(this.cfg);
  }

  connectAuthRuntime(): Promise<CmmdAuthRuntimeConnectDescriptor> {
    return connectAuthRuntime(this.cfg);
  }

  getUserCodexCredential(): Promise<CmmdCodexCredentialState> {
    return getUserCodexCredential(this.cfg);
  }

  disconnectUserCodexCredential(): Promise<CmmdCodexCredentialState> {
    return disconnectUserCodexCredential(this.cfg);
  }

  getUserClaudeCredential(): Promise<CmmdClaudeCredentialState> {
    return getUserClaudeCredential(this.cfg);
  }

  async getProviderAccounts(): Promise<CmmdProviderAccountInventory> {
    return request<CmmdProviderAccountInventory>(
      { method: "GET", path: CmmdApiClient.PROVIDER_ACCOUNTS_PATH },
      this.cfg,
    );
  }

  async removeLinkedProviderAccount(id: string | number): Promise<{ readonly removed: boolean }> {
    return removeLinkedProviderAccount(this.cfg, id);
  }

  async setProviderAccountDefault(params: {
    readonly provider: "codex" | "claudeAgent";
    readonly providerAccountId: string;
  }): Promise<CmmdProviderAccountInventory> {
    return request<CmmdProviderAccountInventory>(
      {
        method: "PUT",
        path: `${CmmdApiClient.PROVIDER_ACCOUNTS_PATH}/default`,
        body: {
          provider: params.provider,
          provider_account_id: params.providerAccountId,
        },
      },
      this.cfg,
    );
  }

  /** Last-known 5h/7d rate-limit usage for the connected Codex and Claude accounts. */
  async getProviderUsage(): Promise<CmmdProviderUsageSnapshots> {
    return request<CmmdProviderUsageSnapshots>(
      { method: "GET", path: CmmdApiClient.PROVIDER_USAGE_PATH },
      this.cfg,
    );
  }

  /**
   * Force a refresh. Codex actively polls chatgpt.com/backend-api/wham/usage;
   * Claude asks CMMD to run the official zero-turn usage command in the
   * account owner's provisioned Environment.
   */
  async refreshProviderUsage(
    provider: "codex" | "claude",
    providerAccountId: string,
  ): Promise<CmmdProviderUsageSnapshots> {
    return request<CmmdProviderUsageSnapshots>(
      {
        method: "POST",
        path: CmmdApiClient.PROVIDER_USAGE_REFRESH_PATH,
        body: { provider, providerAccountId },
      },
      this.cfg,
    );
  }

  disconnectUserClaudeCredential(): Promise<CmmdClaudeCredentialState> {
    return disconnectUserClaudeCredential(this.cfg);
  }

  async getClaudeRuntimeReadiness(runtimeId: string): Promise<CmmdClaudeRuntimeReadiness> {
    return request<CmmdClaudeRuntimeReadiness>(
      {
        method: "GET",
        path: `/api/cmmd/providers/claude/runtime-readiness/${encodeURIComponent(runtimeId)}`,
      },
      this.cfg,
    );
  }

  async appendRuntimeThreadEvents(
    runtimeId: string,
    threadId: string,
    params: AppendCmmdRuntimeThreadEventsParams,
  ): Promise<AppendCmmdRuntimeThreadEventsResult> {
    return request<AppendCmmdRuntimeThreadEventsResult>(
      {
        method: "POST",
        path: `/api/cmmd/devices/runtimes/${encodeURIComponent(runtimeId)}/threads/${encodeURIComponent(threadId)}/events`,
        body: params,
      },
      this.cfg,
    );
  }

  async getRuntimeThreadReplica(
    runtimeId: string,
    threadId: string,
  ): Promise<CmmdRuntimeThreadReplica> {
    if (isEmbedThreadViewerExample(runtimeId, threadId)) return embedThreadViewerExampleReplica();
    return request<CmmdRuntimeThreadReplica>(
      {
        method: "GET",
        path: `/api/cmmd/threads/${encodeURIComponent(runtimeId)}/${encodeURIComponent(threadId)}`,
      },
      this.cfg,
    );
  }

  renameCloudEnvironment(runtimeId: string, displayName: string | null) {
    return renameCloudEnvironment(this.cfg, runtimeId, displayName);
  }

  renameDevice(deviceId: number, displayName: string) {
    return renameDevice(this.cfg, deviceId, displayName);
  }

  revokeDevice(deviceId: number) {
    return revokeDevice(this.cfg, deviceId);
  }

  // ── Simulator hosts and leases ─────────────────────────────────────────────

  private static readonly SIM_PATH = "/api/cmmd/sim-host";

  /** `?orgId=` when the caller named a workspace, otherwise nothing. */
  private static simScope(params: CmmdSimScopeParams | undefined): string {
    return params?.orgId === undefined ? "" : `?orgId=${encodeURIComponent(String(params.orgId))}`;
  }

  async listSimHosts(params?: CmmdSimScopeParams): Promise<ReadonlyArray<CmmdSimHost>> {
    return request<ReadonlyArray<CmmdSimHost>>(
      {
        method: "GET",
        path: `${CmmdApiClient.SIM_PATH}/hosts${CmmdApiClient.simScope(params)}`,
      },
      this.cfg,
    );
  }

  /**
   * The caller's lease for one thread, or null when none has been claimed.
   *
   * Null is the ordinary starting state, not an error — CMMD answers 200 with a
   * null body rather than 404 so a poll loop does not treat "nothing yet" as a
   * failure.
   */
  async getSimLease(params: CmmdSimLeaseLookupParams): Promise<CmmdSimLease | null> {
    const query = new URLSearchParams({
      forgeEnvironmentId: params.forgeEnvironmentId,
      threadId: params.threadId,
      ...(params.orgId === undefined ? {} : { orgId: String(params.orgId) }),
    });
    return request<CmmdSimLease | null>(
      { method: "GET", path: `${CmmdApiClient.SIM_PATH}/leases?${query.toString()}` },
      this.cfg,
    );
  }

  async claimSimLease(params: CmmdSimLeaseClaimParams): Promise<CmmdSimLease> {
    return request<CmmdSimLease>(
      {
        method: "POST",
        path: `${CmmdApiClient.SIM_PATH}/leases${CmmdApiClient.simScope(params)}`,
        // No userId: the lease is keyed to the authenticated session, and CMMD
        // ignores a user id in the body precisely so one owner cannot claim a
        // simulator as another.
        body: {
          hostId: params.hostId,
          forgeEnvironmentId: params.forgeEnvironmentId,
          threadId: params.threadId,
          ttlMs: params.ttlMs,
        },
      },
      this.cfg,
    );
  }

  async renewSimLease(params: CmmdSimLeaseFenceParams & { ttlMs: number }): Promise<CmmdSimLease> {
    return request<CmmdSimLease>(
      {
        method: "POST",
        path: `${CmmdApiClient.SIM_PATH}/leases/${encodeURIComponent(String(params.leaseId))}/renew${CmmdApiClient.simScope(params)}`,
        body: {
          generation: params.generation,
          rowVersion: params.rowVersion,
          ttlMs: params.ttlMs,
        },
      },
      this.cfg,
    );
  }

  async releaseSimLease(params: CmmdSimLeaseFenceParams): Promise<void> {
    await request<{ released: boolean }>(
      {
        method: "POST",
        path: `${CmmdApiClient.SIM_PATH}/leases/${encodeURIComponent(String(params.leaseId))}/release${CmmdApiClient.simScope(params)}`,
        body: { generation: params.generation, rowVersion: params.rowVersion },
      },
      this.cfg,
    );
  }

  // ── Campaigns ──────────────────────────────────────────────────────────────

  async listCampaigns(params?: {
    orgId?: string;
    allWorkspaces?: boolean;
    signal?: AbortSignal;
  }): Promise<ReadonlyArray<CmmdCampaign>> {
    const query: Record<string, string | undefined> = {};
    if (params?.orgId) query["orgId"] = params.orgId;
    if (params?.allWorkspaces) query["allWorkspaces"] = "true";
    const result = await request<unknown>(
      {
        method: "GET",
        path: "/api/cmmd/campaigns",
        query,
        ...(params?.signal ? { signal: params.signal } : {}),
      },
      this.cfg,
    );
    return readCmmdCollection<CmmdCampaign>(result, "campaigns").map(normalizeCmmdCampaign);
  }

  async getCampaign(id: string): Promise<CmmdCampaign> {
    return request<CmmdCampaign>({ method: "GET", path: `/api/cmmd/campaigns/${id}` }, this.cfg);
  }

  async createCampaign(params: CreateCampaignParams): Promise<CmmdCampaign> {
    return request<CmmdCampaign>(
      { method: "POST", path: "/api/cmmd/campaigns", body: params },
      this.cfg,
    );
  }

  async updateCampaign(id: string, patch: UpdateCampaignParams): Promise<CmmdCampaign> {
    return request<CmmdCampaign>(
      { method: "PUT", path: `/api/cmmd/campaigns/${id}`, body: patch },
      this.cfg,
    );
  }

  async deleteCampaign(id: string): Promise<void> {
    await request<void>({ method: "DELETE", path: `/api/cmmd/campaigns/${id}` }, this.cfg);
  }

  async listProjects(params?: {
    campaignId?: string;
    orgId?: string;
    allWorkspaces?: boolean;
    signal?: AbortSignal;
  }): Promise<ReadonlyArray<CmmdProject>> {
    const query: Record<string, string | undefined> = {};
    if (params?.campaignId) query["campaignId"] = params.campaignId;
    if (params?.orgId) query["orgId"] = params.orgId;
    if (params?.allWorkspaces) query["allWorkspaces"] = "true";
    const result = await request<unknown>(
      {
        method: "GET",
        path: "/api/cmmd/projects",
        query,
        ...(params?.signal ? { signal: params.signal } : {}),
      },
      this.cfg,
    );
    return readCmmdCollection<CmmdProject>(result, "projects").map(normalizeCmmdProject);
  }

  async listTasks(params?: {
    projectId?: string;
    status?: string;
  }): Promise<ReadonlyArray<CmmdTask>> {
    const query: Record<string, string | undefined> = {};
    if (params?.projectId) query["projectId"] = params.projectId;
    if (params?.status) query["status"] = params.status;
    return request<ReadonlyArray<CmmdTask>>(
      { method: "GET", path: "/api/cmmd/tasks", query },
      this.cfg,
    );
  }

  async getProject(id: string): Promise<CmmdProject> {
    const result = await request<CmmdProject>(
      { method: "GET", path: `/api/cmmd/projects/${id}` },
      this.cfg,
    );
    return normalizeCmmdProject(result);
  }

  async getProjectMembers(projectId: string): Promise<ReadonlyArray<CmmdMember>> {
    const rows = await request<ReadonlyArray<unknown>>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/members` },
      this.cfg,
    );
    return Array.isArray(rows) ? rows.map(normalizeCmmdMember) : [];
  }

  async listWorkspaceMembers(): Promise<ReadonlyArray<CmmdMember>> {
    const rows = await request<ReadonlyArray<unknown>>(
      { method: "GET", path: "/api/cmmd/members" },
      this.cfg,
    );
    return Array.isArray(rows) ? rows.map(normalizeCmmdWorkspaceMember) : [];
  }

  async addProjectMember(
    projectId: string,
    params: AddCmmdProjectMemberParams,
  ): Promise<AddCmmdProjectMemberResult> {
    return request<AddCmmdProjectMemberResult>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/members`, body: params },
      this.cfg,
    );
  }

  async listProjectAgentMembers(projectId: string): Promise<ReadonlyArray<CmmdProjectAgentMember>> {
    return request<ReadonlyArray<CmmdProjectAgentMember>>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/agent-members` },
      this.cfg,
    );
  }

  async createProjectAgentMember(
    projectId: string,
    params: CreateCmmdProjectAgentMemberParams,
  ): Promise<CreateCmmdProjectAgentMemberResult> {
    return request<CreateCmmdProjectAgentMemberResult>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/agent-members`, body: params },
      this.cfg,
    );
  }

  async renameProjectAgentMember(
    projectId: string,
    memberId: number,
    params: RenameCmmdProjectAgentMemberParams,
  ): Promise<RenameCmmdProjectAgentMemberResult> {
    return request<RenameCmmdProjectAgentMemberResult>(
      {
        method: "PATCH",
        path: `/api/cmmd/projects/${projectId}/agent-members/${memberId}`,
        body: params,
      },
      this.cfg,
    );
  }

  async retireProjectAgentMember(
    projectId: string,
    memberId: number,
    params: RetireCmmdProjectAgentMemberParams,
  ): Promise<RetireCmmdProjectAgentMemberResult> {
    return request<RetireCmmdProjectAgentMemberResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/agent-members/${memberId}/retire`,
        body: params,
      },
      this.cfg,
    );
  }

  async updateProjectAgentMemberAvatar(
    projectId: string,
    memberId: number,
    params: UpdateCmmdProjectAgentMemberAvatarParams,
  ): Promise<UpdateCmmdProjectAgentMemberAvatarResult> {
    return request<UpdateCmmdProjectAgentMemberAvatarResult>(
      {
        method: "PUT",
        path: `/api/cmmd/projects/${projectId}/agent-members/${memberId}/avatar`,
        body: params,
      },
      this.cfg,
    );
  }

  async listProjectAgentMemberInstances(
    projectId: string,
    memberId: number,
  ): Promise<ReadonlyArray<CmmdProjectAgentInstance>> {
    return request<ReadonlyArray<CmmdProjectAgentInstance>>(
      {
        method: "GET",
        path: `/api/cmmd/projects/${projectId}/agent-members/${memberId}/instances`,
      },
      this.cfg,
    );
  }

  async previewProjectAgentMemberConsolidation(
    projectId: string,
  ): Promise<CmmdProjectAgentMemberConsolidationPreview> {
    return request<CmmdProjectAgentMemberConsolidationPreview>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/agent-members/consolidation/preview`,
      },
      this.cfg,
    );
  }

  async applyProjectAgentMemberConsolidation(
    projectId: string,
    params: ApplyCmmdProjectAgentMemberConsolidationParams,
  ): Promise<CmmdProjectAgentMemberConsolidationResult> {
    return request<CmmdProjectAgentMemberConsolidationResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/agent-members/consolidation/apply`,
        body: params,
      },
      this.cfg,
    );
  }

  async setProjectTaskAssignment(
    projectId: string,
    taskId: number,
    params: CmmdProjectTaskAssignmentParams,
  ): Promise<CmmdProjectTaskAssignmentResult> {
    return request<CmmdProjectTaskAssignmentResult>(
      {
        method: "PUT",
        path: `/api/cmmd/projects/${projectId}/tasks/${taskId}/assignment`,
        body: params,
      },
      this.cfg,
    );
  }

  async getProjectUpdates(
    projectId: string,
    params?: { readonly page?: number; readonly limit?: number },
  ): Promise<CmmdProjectUpdatesPage> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    const envelope = await request<CmmdEnvelope<ReadonlyArray<unknown>>>(
      {
        method: "GET",
        path: `/api/cmmd/projects/${projectId}/updates`,
        query: { page, limit },
        unwrapEnvelope: false,
      },
      this.cfg,
    );
    return normalizeProjectUpdatesPage(envelope, page, limit);
  }

  async createProjectUpdate(
    projectId: string,
    input: { status: CmmdProjectUpdateStatus; body: string },
  ): Promise<CmmdProjectUpdateView> {
    const row = await request<unknown>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/updates`, body: input },
      this.cfg,
    );
    return normalizeCmmdProjectUpdate(row);
  }

  async updateProjectUpdate(
    projectId: string,
    updateId: string,
    input: { status: CmmdProjectUpdateStatus; body: string },
  ): Promise<CmmdProjectUpdateView> {
    const row = await request<unknown>(
      {
        method: "PATCH",
        path: `/api/cmmd/projects/${projectId}/updates/${updateId}`,
        body: input,
      },
      this.cfg,
    );
    return normalizeCmmdProjectUpdate(row);
  }

  async deleteProjectUpdate(projectId: string, updateId: string): Promise<void> {
    await request<void>(
      { method: "DELETE", path: `/api/cmmd/projects/${projectId}/updates/${updateId}` },
      this.cfg,
    );
  }

  async listProjectMilestones(projectId: string): Promise<ReadonlyArray<CmmdProjectMilestone>> {
    return request<ReadonlyArray<CmmdProjectMilestone>>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/milestones` },
      this.cfg,
    );
  }

  async createProjectMilestone(
    projectId: string,
    input: CreateCmmdProjectMilestoneParams,
  ): Promise<CmmdProjectMilestone> {
    return request<CmmdProjectMilestone>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/milestones`, body: input },
      this.cfg,
    );
  }

  async updateProjectMilestone(
    projectId: string,
    milestoneId: number,
    input: UpdateCmmdProjectMilestoneParams,
  ): Promise<CmmdProjectMilestone> {
    return request<CmmdProjectMilestone>(
      {
        method: "PATCH",
        path: `/api/cmmd/projects/${projectId}/milestones/${milestoneId}`,
        body: input,
      },
      this.cfg,
    );
  }

  async deleteProjectMilestone(projectId: string, milestoneId: number): Promise<void> {
    await request<void>(
      {
        method: "DELETE",
        path: `/api/cmmd/projects/${projectId}/milestones/${milestoneId}`,
      },
      this.cfg,
    );
  }

  async improveProjectContent(
    projectId: string,
    input: { surface: CmmdProjectContentSurface; content: string },
  ): Promise<CmmdProjectContentReadabilityResult> {
    return request<CmmdProjectContentReadabilityResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/content/readability`,
        body: input,
      },
      this.cfg,
    );
  }

  async listProjectBoard(projectId: string): Promise<ReadonlyArray<CmmdBoardTask>> {
    // Registry route (unwrapData) → server strips CMMD envelope → array of tasks
    return request<ReadonlyArray<CmmdBoardTask>>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/board` },
      this.cfg,
    );
  }

  async createProjectTaskDraft(
    projectId: string,
    params: CreateCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraft> {
    return request<CmmdProjectTaskDraft>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/task-drafts`, body: params },
      this.cfg,
    );
  }

  async getProjectTaskDraft(projectId: string, draftId: number): Promise<CmmdProjectTaskDraft> {
    return request<CmmdProjectTaskDraft>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/task-drafts/${draftId}` },
      this.cfg,
    );
  }

  async updateProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: UpdateCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraft> {
    return request<CmmdProjectTaskDraft>(
      {
        method: "PATCH",
        path: `/api/cmmd/projects/${projectId}/task-drafts/${draftId}`,
        body: params,
      },
      this.cfg,
    );
  }

  async previewProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: PreviewCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraftPreview> {
    return request<CmmdProjectTaskDraftPreview>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/task-drafts/${draftId}/preview`,
        body: params,
      },
      this.cfg,
    );
  }

  async commitProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: CommitCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraftCommit> {
    return request<CmmdProjectTaskDraftCommit>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/task-drafts/${draftId}/commit`,
        body: params,
      },
      this.cfg,
    );
  }

  async discardProjectTaskDraft(
    projectId: string,
    draftId: number,
    params: DiscardCmmdProjectTaskDraftParams,
  ): Promise<CmmdProjectTaskDraft> {
    return request<CmmdProjectTaskDraft>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/task-drafts/${draftId}/discard`,
        body: params,
      },
      this.cfg,
    );
  }

  async getProjectTaskWorkflow(projectId: string): Promise<CmmdTaskWorkflow> {
    return request<CmmdTaskWorkflow>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/task-workflow` },
      this.cfg,
    );
  }

  async updateProjectTaskWorkflow(
    projectId: string,
    workflow: CmmdTaskWorkflow,
  ): Promise<CmmdTaskWorkflow> {
    return request<CmmdTaskWorkflow>(
      { method: "PUT", path: `/api/cmmd/projects/${projectId}/task-workflow`, body: workflow },
      this.cfg,
    );
  }

  async getProjectLeadPlan(projectId: string): Promise<CmmdProjectLeadPlan> {
    return request<CmmdProjectLeadPlan>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/plan` },
      this.cfg,
    );
  }

  async activateProjectLeadAutomaticStaffing(
    projectId: string,
  ): Promise<ActivateCmmdProjectLeadAuthorityResult> {
    return request<ActivateCmmdProjectLeadAuthorityResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/authority/activate`,
        body: {},
      },
      this.cfg,
    );
  }

  async getProjectLeadReadiness(projectId: string): Promise<CmmdProjectLeadReadiness> {
    return request<CmmdProjectLeadReadiness>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/readiness` },
      this.cfg,
    );
  }

  async getProjectLeadCapabilities(projectId: string): Promise<CmmdProjectLeadCapabilities> {
    return request<CmmdProjectLeadCapabilities>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/capabilities` },
      this.cfg,
    );
  }

  async getProjectLeadSummary(projectId: string): Promise<CmmdProjectLeadSummary> {
    return request<CmmdProjectLeadSummary>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/summary` },
      this.cfg,
    );
  }

  async getProjectLeadDetail(projectId: string): Promise<CmmdProjectLeadDetail> {
    return request<CmmdProjectLeadDetail>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/detail` },
      this.cfg,
    );
  }

  async getProjectLeadBriefing(projectId: string): Promise<CmmdProjectLeadBriefing> {
    return request<CmmdProjectLeadBriefing>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/briefing` },
      this.cfg,
    );
  }

  async decideProjectLeadApproval(
    projectId: string,
    approvalId: string,
    params: DecideCmmdProjectLeadApprovalParams,
  ): Promise<DecideCmmdProjectLeadApprovalResult> {
    return request<DecideCmmdProjectLeadApprovalResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/approvals/${approvalId}/decision`,
        body: params,
      },
      this.cfg,
    );
  }

  async listProjectLeadEvents(
    projectId: string,
    params?: CmmdProjectLeadEventsParams,
  ): Promise<CmmdProjectLeadEvents> {
    return request<CmmdProjectLeadEvents>(
      {
        method: "GET",
        path: `/api/cmmd/projects/${projectId}/project-lead/events`,
        ...(params
          ? {
              query: {
                ...(params.afterEventId !== undefined
                  ? { afterEventId: String(params.afterEventId) }
                  : {}),
                ...(params.latest !== undefined ? { latest: String(params.latest) } : {}),
                ...(params.limit !== undefined ? { limit: String(params.limit) } : {}),
              },
            }
          : {}),
      },
      this.cfg,
    );
  }

  async explainProjectLead(projectId: string): Promise<CmmdProjectLeadExplanation> {
    return request<CmmdProjectLeadExplanation>(
      { method: "GET", path: `/api/cmmd/projects/${projectId}/project-lead/explain` },
      this.cfg,
    );
  }

  async pauseProjectLead(
    projectId: string,
    params: PauseCmmdProjectLeadParams,
  ): Promise<PauseCmmdProjectLeadResult> {
    return request<PauseCmmdProjectLeadResult>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/project-lead/pause`, body: params },
      this.cfg,
    );
  }

  async resumeProjectLead(
    projectId: string,
    params: ResumeCmmdProjectLeadParams,
  ): Promise<ResumeCmmdProjectLeadResult> {
    return request<ResumeCmmdProjectLeadResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/resume`,
        body: params,
      },
      this.cfg,
    );
  }

  async reserveProjectLeadTask(
    projectId: string,
    taskId: number,
    params: ReserveCmmdProjectLeadTaskParams,
  ): Promise<ReserveCmmdProjectLeadTaskResult> {
    return request<ReserveCmmdProjectLeadTaskResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/tasks/${taskId}/reservations`,
        body: params,
      },
      this.cfg,
    );
  }

  async releaseProjectLeadReservation(
    projectId: string,
    reservationId: number,
    params: ReleaseCmmdProjectLeadReservationParams,
  ): Promise<ReleaseCmmdProjectLeadReservationResult> {
    return request<ReleaseCmmdProjectLeadReservationResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/reservations/${reservationId}/release`,
        body: params,
      },
      this.cfg,
    );
  }

  async reconcileProjectLead(
    projectId: string,
    params: ReconcileCmmdProjectLeadParams,
  ): Promise<ReconcileCmmdProjectLeadResult> {
    return request<ReconcileCmmdProjectLeadResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/reconcile`,
        body: params,
      },
      this.cfg,
    );
  }

  async resolveProjectLeadAttention(
    projectId: string,
    attentionId: number,
    params: ResolveCmmdProjectLeadAttentionParams,
  ): Promise<ResolveCmmdProjectLeadAttentionResult> {
    return request<ResolveCmmdProjectLeadAttentionResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/project-lead/attention/${attentionId}/resolve`,
        body: params,
      },
      this.cfg,
    );
  }

  async listCenterBoard(params?: ListCmmdCenterBoardParams): Promise<CmmdCenterBoardResponse> {
    const query: Record<string, string | undefined> = {};
    if (params?.status) query["status"] = params.status;
    if (params?.workspaceId) query["workspaceId"] = String(params.workspaceId);
    if (params?.campaignId) query["campaignId"] = String(params.campaignId);
    if (params?.ownerId) query["ownerId"] = String(params.ownerId);
    if (params?.includeArchived !== undefined) query["includeArchived"] = params.includeArchived;
    if (params?.q) query["q"] = params.q;
    return request<CmmdCenterBoardResponse>(
      { method: "GET", path: "/api/cmmd/work-items/board", query },
      this.cfg,
    );
  }

  async listForgeAutomationPresets(): Promise<ReadonlyArray<CmmdForgeAutomationPreset>> {
    const result = await request<unknown>(
      { method: "GET", path: "/api/cmmd/automations/presets" },
      this.cfg,
    );
    return readCmmdAutomationCollection<CmmdForgeAutomationPreset>(result, "presets");
  }

  async listUserForgeAutomationRules(): Promise<ReadonlyArray<CmmdUserForgeAutomationRule>> {
    const result = await request<unknown>(
      { method: "GET", path: "/api/cmmd/automations/user/rules" },
      this.cfg,
    );
    return readCmmdAutomationCollection<CmmdUserForgeAutomationRule>(result, "rules");
  }

  async createUserForgeAutomationRule(
    params: CmmdCreateUserForgeAutomationRuleParams,
  ): Promise<CmmdUserForgeAutomationRule> {
    return request<CmmdUserForgeAutomationRule>(
      { method: "POST", path: "/api/cmmd/automations/user/rules", body: params },
      this.cfg,
    );
  }

  async updateUserForgeAutomationRule(
    ruleId: number,
    params: CmmdUpdateUserForgeAutomationRuleParams,
  ): Promise<CmmdUserForgeAutomationRule> {
    return request<CmmdUserForgeAutomationRule>(
      { method: "PATCH", path: `/api/cmmd/automations/user/rules/${ruleId}`, body: params },
      this.cfg,
    );
  }

  async listUserForgeAutomationRuns(
    ruleId: number,
  ): Promise<ReadonlyArray<CmmdUserForgeAutomationRun>> {
    const result = await request<unknown>(
      { method: "GET", path: `/api/cmmd/automations/user/rules/${ruleId}/runs` },
      this.cfg,
    );
    return readCmmdAutomationCollection<CmmdUserForgeAutomationRun>(result, "runs");
  }

  async startUserForgeAutomationRun(
    ruleId: number,
    params: CmmdStartUserForgeAutomationRunParams,
  ): Promise<CmmdStartUserForgeAutomationRunResult> {
    return request<CmmdStartUserForgeAutomationRunResult>(
      { method: "POST", path: `/api/cmmd/automations/user/rules/${ruleId}/runs`, body: params },
      this.cfg,
    );
  }

  async listForgeAutomationRules(
    projectId: string,
    scope?: CmmdForgeAutomationScope,
  ): Promise<ReadonlyArray<CmmdForgeAutomationRule>> {
    const query = forgeAutomationQuery(scope);
    const result = await request<unknown>(
      {
        method: "GET",
        path: `/api/cmmd/automations/projects/${projectId}/rules`,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
    return readCmmdAutomationCollection<CmmdForgeAutomationRule>(result, "rules");
  }

  async createForgeAutomationRule(
    projectId: string,
    params: CmmdCreateForgeAutomationRuleParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdForgeAutomationRule> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdForgeAutomationRule>(
      {
        method: "POST",
        path: `/api/cmmd/automations/projects/${projectId}/rules`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async updateForgeAutomationRule(
    projectId: string,
    ruleId: number,
    params: CmmdUpdateForgeAutomationRuleParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdForgeAutomationRule> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdForgeAutomationRule>(
      {
        method: "PATCH",
        path: `/api/cmmd/automations/projects/${projectId}/rules/${ruleId}`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async listForgeAutomationRuns(
    projectId: string,
    params?: CmmdForgeAutomationScope & { readonly taskId?: number },
  ): Promise<ReadonlyArray<CmmdForgeAutomationRun>> {
    const query = forgeAutomationQuery(params, params?.taskId);
    const result = await request<unknown>(
      {
        method: "GET",
        path: `/api/cmmd/automations/projects/${projectId}/runs`,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
    return readCmmdAutomationCollection<CmmdForgeAutomationRun>(result, "runs");
  }

  async getForgeAutomationRun(
    runId: number,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdForgeAutomationRunDetail> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdForgeAutomationRunDetail>(
      {
        method: "GET",
        path: `/api/cmmd/automations/runs/${runId}`,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async startForgeAutomationRun(
    projectId: string,
    taskId: number,
    params: CmmdStartForgeAutomationRunParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdStartForgeAutomationRunResult> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdStartForgeAutomationRunResult>(
      {
        method: "POST",
        path: `/api/cmmd/automations/projects/${projectId}/tasks/${taskId}/runs`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async retryForgeAutomationRun(
    runId: number,
    params: CmmdRetryForgeAutomationRunParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdRetryForgeAutomationRunResult> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdRetryForgeAutomationRunResult>(
      {
        method: "POST",
        path: `/api/cmmd/automations/runs/${runId}/retry`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async cancelForgeAutomationRun(
    runId: number,
    params: CmmdCancelForgeAutomationRunParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdCancelForgeAutomationRunResult> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdCancelForgeAutomationRunResult>(
      {
        method: "POST",
        path: `/api/cmmd/automations/runs/${runId}/cancel`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async approveForgeAutomationEscalation(
    runId: number,
    params: CmmdApproveForgeAutomationEscalationParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdApproveForgeAutomationEscalationResult> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdApproveForgeAutomationEscalationResult>(
      {
        method: "POST",
        path: `/api/cmmd/automations/runs/${runId}/escalations/approve`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async rejectForgeAutomationEscalation(
    runId: number,
    params: CmmdRejectForgeAutomationEscalationParams,
    scope?: CmmdForgeAutomationScope,
  ): Promise<CmmdRejectForgeAutomationEscalationResult> {
    const query = forgeAutomationQuery(scope);
    return request<CmmdRejectForgeAutomationEscalationResult>(
      {
        method: "POST",
        path: `/api/cmmd/automations/runs/${runId}/escalations/reject`,
        body: params,
        ...(query ? { query } : {}),
      },
      this.cfg,
    );
  }

  async createProjectBoardTask(
    projectId: string,
    params: CreateCmmdBoardTaskParams,
  ): Promise<CmmdBoardTask> {
    const body: Record<string, unknown> = { title: params.title };
    if (params.description !== undefined) body["description"] = params.description;
    if (params.priority !== undefined) body["priority"] = params.priority;
    if (params.status !== undefined) body["status"] = params.status;
    if (params.workflowStageId !== undefined) body["workflowStageId"] = params.workflowStageId;
    if (params.assignee !== undefined) body["assignee"] = params.assignee;
    if (params.dueDate !== undefined) body["dueDate"] = params.dueDate;
    if (params.workItemType !== undefined) body["workItemType"] = params.workItemType;
    if (params.parentTaskId !== undefined) body["parentTaskId"] = params.parentTaskId;
    if (params.milestoneId !== undefined) body["milestoneId"] = params.milestoneId;
    if (params.dependsOn !== undefined) body["dependsOn"] = params.dependsOn;
    return request<CmmdBoardTask>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/board`, body },
      this.cfg,
    );
  }

  async createCenterBoardTask(params: CreateCmmdCenterBoardTaskParams): Promise<CmmdBoardTask> {
    const body: Record<string, unknown> = { title: params.title, projectId: params.projectId };
    if (params.description !== undefined) body["description"] = params.description;
    if (params.priority !== undefined) body["priority"] = params.priority;
    if (params.status !== undefined) body["status"] = params.status;
    if (params.assignee !== undefined) body["assignee"] = params.assignee;
    if (params.dueDate !== undefined) body["dueDate"] = params.dueDate;
    if (params.workItemType !== undefined) body["workItemType"] = params.workItemType;
    if (params.parentTaskId !== undefined) body["parentTaskId"] = params.parentTaskId;
    if (params.milestoneId !== undefined) body["milestoneId"] = params.milestoneId;
    if (params.dependsOn !== undefined) body["dependsOn"] = params.dependsOn;
    return request<CmmdBoardTask>(
      { method: "POST", path: "/api/cmmd/work-items/board", body },
      this.cfg,
    );
  }

  async submitFeedback(params: CreateCmmdFeedbackParams): Promise<CmmdFeedbackIntakeResult> {
    const body: Record<string, unknown> = {
      type: params.type,
      title: params.title,
      source: params.source,
    };
    if (params.description !== undefined) body["description"] = params.description;
    if (params.priority !== undefined) body["priority"] = params.priority;
    if (params.senderName !== undefined) body["senderName"] = params.senderName;
    return request<CmmdFeedbackIntakeResult>(
      { method: "POST", path: "/api/cmmd/feedback", body },
      this.cfg,
    );
  }

  async updateProjectBoardTask(
    projectId: string,
    taskId: number,
    patch: UpdateCmmdBoardTaskParams,
  ): Promise<CmmdBoardTask> {
    return request<CmmdBoardTask>(
      { method: "PATCH", path: `/api/cmmd/projects/${projectId}/board/${taskId}`, body: patch },
      this.cfg,
    );
  }

  async updateCenterBoardTask(
    taskId: number,
    patch: UpdateCmmdBoardTaskParams,
  ): Promise<CmmdBoardTask> {
    return request<CmmdBoardTask>(
      { method: "PATCH", path: `/api/cmmd/work-items/board/${taskId}`, body: patch },
      this.cfg,
    );
  }

  async moveCenterBoardTask(
    taskId: number,
    params: MoveCmmdCenterBoardTaskParams,
  ): Promise<CmmdBoardTask> {
    const body: Record<string, unknown> = { toStatus: params.toStatus };
    if (params.beforeTaskId !== undefined) body["beforeTaskId"] = params.beforeTaskId;
    if (params.afterTaskId !== undefined) body["afterTaskId"] = params.afterTaskId;
    return request<CmmdBoardTask>(
      { method: "PATCH", path: `/api/cmmd/work-items/board/${taskId}/move`, body },
      this.cfg,
    );
  }

  async moveProjectBoardTask(
    projectId: string,
    taskId: number,
    params: MoveCmmdProjectBoardTaskParams,
  ): Promise<CmmdBoardTask> {
    const body: Record<string, unknown> = { toStatus: params.toStatus };
    if (params.toStageId !== undefined) body["toStageId"] = params.toStageId;
    if (params.beforeTaskId !== undefined) body["beforeTaskId"] = params.beforeTaskId;
    if (params.afterTaskId !== undefined) body["afterTaskId"] = params.afterTaskId;
    return request<CmmdBoardTask>(
      {
        method: "PATCH",
        path: `/api/cmmd/projects/${projectId}/board/${taskId}/move`,
        body,
      },
      this.cfg,
    );
  }

  async archiveProjectBoardTask(projectId: string, taskId: number): Promise<CmmdBoardTask> {
    return request<CmmdBoardTask>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/board/${taskId}/archive` },
      this.cfg,
    );
  }

  async unarchiveProjectBoardTask(projectId: string, taskId: number): Promise<CmmdBoardTask> {
    return request<CmmdBoardTask>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/board/${taskId}/unarchive` },
      this.cfg,
    );
  }

  async updateProjectBoardTaskStatus(
    projectId: string,
    taskId: number,
    status: CmmdTaskStatus,
    workflowStageId?: string,
  ): Promise<CmmdBoardTask> {
    return request<CmmdBoardTask>(
      {
        method: "PATCH",
        path: `/api/cmmd/projects/${projectId}/board/${taskId}/status`,
        body: { status, ...(workflowStageId ? { workflowStageId } : {}) },
      },
      this.cfg,
    );
  }

  async listTaskExecutionOptions(
    projectId: string,
    taskId: number,
  ): Promise<ReadonlyArray<CmmdTaskExecutionOption>> {
    return request<ReadonlyArray<CmmdTaskExecutionOption>>(
      {
        method: "GET",
        path: `/api/cmmd/projects/${projectId}/board/${taskId}/execution-options`,
      },
      this.cfg,
    );
  }

  async createTaskWorkSession(
    projectId: string,
    taskId: number,
    params: CmmdCreateWorkSessionParams,
  ): Promise<CmmdCreateWorkSessionResult> {
    return request<CmmdCreateWorkSessionResult>(
      {
        method: "POST",
        path: `/api/cmmd/projects/${projectId}/board/${taskId}/work-sessions`,
        body: params,
      },
      this.cfg,
    );
  }

  async listProjectWorkSessions(
    projectId: string,
    params: CmmdListWorkSessionsParams = {},
  ): Promise<ReadonlyArray<CmmdWorkSession>> {
    return request<ReadonlyArray<CmmdWorkSession>>(
      {
        method: "GET",
        path: `/api/cmmd/projects/${projectId}/work-sessions`,
        query: params,
      },
      this.cfg,
    );
  }

  async getWorkSession(workSessionId: string): Promise<CmmdWorkSession> {
    return request<CmmdWorkSession>(
      { method: "GET", path: `/api/cmmd/work-sessions/${workSessionId}` },
      this.cfg,
    );
  }

  async updateWorkSessionStatus(
    workSessionId: string,
    status: CmmdWorkSessionStatus,
  ): Promise<CmmdWorkSession> {
    return request<CmmdWorkSession>(
      {
        method: "PATCH",
        path: `/api/cmmd/work-sessions/${workSessionId}`,
        body: { status },
      },
      this.cfg,
    );
  }

  async attachWorkSessionThread(
    workSessionId: string,
    params: CmmdAttachWorkSessionThreadParams,
  ): Promise<CmmdAttachWorkSessionThreadResult> {
    return request<CmmdAttachWorkSessionThreadResult>(
      {
        method: "POST",
        path: `/api/cmmd/work-sessions/${workSessionId}/threads`,
        body: params,
      },
      this.cfg,
    );
  }

  async listTaskConversations(
    taskId: number,
    params: CmmdTaskConversationListParams = {},
  ): Promise<CmmdTaskConversationList> {
    return request<CmmdTaskConversationList>(
      {
        method: "GET",
        path: `/api/cmmd/tasks/${taskId}/conversations`,
        query: taskConversationQuery(params),
      },
      this.cfg,
    );
  }

  async linkTaskConversation(
    taskId: number,
    params: LinkCmmdTaskConversationParams,
  ): Promise<LinkCmmdTaskConversationResult> {
    return request<LinkCmmdTaskConversationResult>(
      {
        method: "POST",
        path: `/api/cmmd/tasks/${taskId}/conversations`,
        body: params,
      },
      this.cfg,
    );
  }

  async getTaskHistory(taskId: number): Promise<CmmdTaskHistory> {
    return request<CmmdTaskHistory>(
      { method: "GET", path: `/api/cmmd/tasks/${taskId}/history` },
      this.cfg,
    );
  }

  async upsertTaskPullRequest(
    taskId: number,
    params: UpsertCmmdTaskPullRequestParams,
  ): Promise<UpsertCmmdTaskPullRequestResult> {
    return request<UpsertCmmdTaskPullRequestResult>(
      {
        method: "PUT",
        path: `/api/cmmd/tasks/${taskId}/pull-requests`,
        body: params,
      },
      this.cfg,
    );
  }

  private postPullRequest<Result>(path: string, body: unknown): Promise<Result> {
    return request<Result>(
      { method: "POST", path: `/api/cmmd/pull-requests/${path}`, body },
      this.cfg,
    );
  }

  async listPullRequests(input: CmmdPullRequestListInput): Promise<CmmdPullRequestListResult> {
    return this.postPullRequest("list", input);
  }

  async getPullRequestListStats(
    input: CmmdPullRequestListStatsInput,
  ): Promise<CmmdPullRequestListStatsResult> {
    return this.postPullRequest("list-stats", input);
  }

  async getPullRequestDetail(input: CmmdPullRequestRef): Promise<CmmdPullRequestDetail> {
    return this.postPullRequest("detail", input);
  }

  async getPullRequestActivity(input: CmmdPullRequestRef): Promise<CmmdPullRequestActivity> {
    return this.postPullRequest("activity", input);
  }

  async getPullRequestDiff(input: CmmdPullRequestDiffInput): Promise<CmmdPullRequestDiffResult> {
    return this.postPullRequest("diff", input);
  }

  async getPullRequestDiffFileContents(
    input: CmmdPullRequestDiffFileContentsInput,
  ): Promise<CmmdPullRequestDiffFileContentsResult> {
    return this.postPullRequest("diff-file-contents", input);
  }

  async getPullRequestThreadComments(
    input: CmmdPullRequestThreadCommentsInput,
  ): Promise<CmmdPullRequestThreadCommentsResult> {
    return this.postPullRequest("thread/comments", input);
  }

  async getPullRequestReviewerCandidates(
    input: CmmdPullRequestReviewerCandidatesInput,
  ): Promise<CmmdPullRequestReviewerCandidatesResult> {
    return this.postPullRequest("reviewer-candidates", input);
  }

  async runPullRequestAction(
    input: CmmdPullRequestActionInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("action", input);
  }

  async createPullRequestComment(
    input: CmmdPullRequestCommentInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("comment", input);
  }

  async updatePullRequest(
    input: CmmdPullRequestUpdateInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("update", input);
  }

  async updatePullRequestComment(
    input: CmmdPullRequestCommentUpdateInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("comment/update", input);
  }

  async submitPullRequestReview(
    input: CmmdPullRequestReviewInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("review", input);
  }

  async replyToPullRequestThread(
    input: CmmdPullRequestThreadReplyInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("thread/reply", input);
  }

  async setPullRequestThreadResolution(
    input: CmmdPullRequestThreadResolutionInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("thread/resolution", input);
  }

  async requestPullRequestReviewers(
    input: CmmdPullRequestReviewersInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("reviewers", input);
  }

  async setPullRequestReaction(
    input: CmmdPullRequestReactionInput,
  ): Promise<CmmdPullRequestMutationResult> {
    return this.postPullRequest("reaction", input);
  }

  async getTaskConversation(
    taskId: number,
    conversationId: string,
    params: CmmdTaskConversationParams = {},
  ): Promise<CmmdTaskConversation> {
    return request<CmmdTaskConversation>(
      {
        method: "GET",
        path: `/api/cmmd/tasks/${taskId}/conversations/${encodeURIComponent(conversationId)}`,
        query: taskConversationQuery(params),
      },
      this.cfg,
    );
  }

  async createTask(params: CreateTaskParams): Promise<CmmdTask> {
    return request<CmmdTask>({ method: "POST", path: "/api/cmmd/tasks", body: params }, this.cfg);
  }

  async createSubtask(_params: CreateSubtaskParams): Promise<CmmdTask> {
    throw new CmmdApiNotImplementedError("createSubtask");
  }

  async createBoardTask(_params: CreateBoardTaskParams): Promise<CmmdTask> {
    throw new CmmdApiNotImplementedError("createBoardTask");
  }

  async createProject(params: CreateProjectParams): Promise<CmmdProject> {
    const body: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) body[key] = value;
    }
    if (params.campaignId !== undefined && params.campaignId !== null) {
      body["campaignId"] = toCmmdMutationId(params.campaignId);
    }
    if (params.organizationId !== undefined && params.organizationId !== null) {
      body["organizationId"] = toCmmdMutationId(params.organizationId);
    }
    const result = await request<unknown>(
      { method: "POST", path: "/api/cmmd/projects", body },
      this.cfg,
    );
    return unwrapProjectMutationResponse(result);
  }

  async moveProject(
    projectId: string,
    params: {
      targetOrgId: string;
      campaignStrategy?: "detach" | "reassign";
      targetCampaignId?: string;
      dryRun?: boolean;
    },
  ): Promise<CmmdMoveReport> {
    const body: Record<string, unknown> = {
      targetOrgId: toCmmdMutationId(params.targetOrgId),
    };
    if (params.campaignStrategy !== undefined) {
      body["campaignStrategy"] = params.campaignStrategy;
    }
    if (params.targetCampaignId !== undefined) {
      body["targetCampaignId"] = toCmmdMutationId(params.targetCampaignId);
    }
    if (params.dryRun !== undefined) {
      body["dryRun"] = params.dryRun;
    }
    return request<CmmdMoveReport>(
      { method: "POST", path: `/api/cmmd/projects/${projectId}/move`, body },
      this.cfg,
    );
  }

  async uploadBrainDoc(_file: File, _meta: BrainDocMeta): Promise<CmmdBrainDoc> {
    throw new CmmdApiNotImplementedError("uploadBrainDoc");
  }

  // ── People ──────────────────────────────────────────────────────────────────
}

// ── Factory + singleton ───────────────────────────────────────────────────────

export function createCmmdApiClient(config?: CmmdApiClientConfig): CmmdApiClientInterface {
  return new CmmdApiClient(config);
}

export const cmmdApiClient: CmmdApiClientInterface = new CmmdApiClient({
  // A real CMMD REST auth failure (401) logs the user out and redirects to
  // re-login instead of stranding them on an error. See cmmdAuthRedirect.ts.
  onAuthExpired: handleCmmdAuthExpired,
});
